// src/services/aeo/onboarding.ts
//
// Client onboarding: from a signed quote to an active client.
//
// One record per client. It holds what was sent, what came back and what
// access we hold, so the staff screen can show one next action and the
// merchant screen can show what is left to do.
//
// PROPOSED BACKEND CONTRACT (none of it exists yet):
//
//   Staff
//   GET   /onboarding                                   -> Onboarding[]
//   GET   /onboarding/prefill?domain=                   -> { outletAreas }  (from the pre-sale scan)
//   GET   /onboarding/form-link?domain=&areas=          -> { link }
//   POST  /onboarding/clients                           -> { businessId, onboarding }
//   GET   /businesses/{id}/onboarding                   -> Onboarding (404 = none)
//   POST  /businesses/{id}/onboarding/messages          -> Onboarding   (send one message)
//   POST  /businesses/{id}/onboarding/release           -> Onboarding   (login + access request)
//   POST  /businesses/{id}/onboarding/login/resend      -> Onboarding   (BE-1: a new link, the old one dies)
//   POST  /businesses/{id}/onboarding/access/{key}/confirm   -> Onboarding
//   POST  /businesses/{id}/onboarding/access/{key}/unconfirm -> Onboarding
//   POST  /businesses/{id}/onboarding/scan/start        -> Onboarding   (staff's go, with the inputs)
//   POST  /businesses/{id}/onboarding/payment           -> Onboarding   (Record the payment: paid_on, plan start)
//   POST  /businesses/{id}/onboarding/signature         -> Onboarding   (O4: staff pick the question it wins)
//   POST  /businesses/{id}/onboarding/first-check       -> Onboarding   (#4773: staff pick another completed scan as the First AI check)
//   GET   /assessments?domain=&status=complete&limit=100&base_path= -> AeoAssessment[]  (the candidates of one site; the caller still filters on base_path)
//
//   Merchant (their own business, from the token)
//   GET   /businesses/mine/onboarding                   -> MerchantOnboarding
//   POST  /businesses/mine/onboarding/access/{key}/claim     -> MerchantOnboarding
//   POST  /businesses/mine/onboarding/access/{key}/unclaim   -> MerchantOnboarding
//   POST  /businesses/mine/onboarding/access/{key}/connect   -> MerchantConnectResult (gsc, ga4)
//   POST  /businesses/mine/onboarding/access/{key}/disconnect -> MerchantOnboarding
//   POST  /businesses/mine/onboarding/share-steps       -> MerchantOnboarding   ({ name, email, steps? })
//   PATCH /businesses/mine/onboarding/form              -> MerchantOnboarding   (one setup: save the answers so far)
//   POST  /businesses/mine/onboarding/form/submit       -> MerchantOnboarding   (one setup: the answers, as the form's)
//
// Until those exist the screens run on the store at the bottom of this file,
// the same way clientReview.ts did before its endpoints shipped. Production
// hides every onboarding screen while the flag is off, so no real user reads
// sample data as their own.

import axios from "axios";
import { getCookie } from "@util/cookie";
import { catchErrorLog } from "@util/sentry";
import type { AeoAssessment, AeoBusiness } from "./types";
import { forgetSampleEngagements, getEngagementIfExists, getTimeline, isClientReviewLive, onSampleTimelinePublished, publishBlockers, seedEngagement, timelinePublishable, updateEngagement, type EngagementSeed } from "./clientReview";
import type { BuyMode, ScanDraftPurpose, ScanDraftStatus } from "./scanDraftTypes";
import { registerMockBusiness } from "./devMock";
import { finishScansFor, firstCheckDraftFor, firstCheckQuestion, forgetFirstCheckDrafts, startedPitchName, startedPitchPrefill } from "./scanDraftMock";
import { scanDraftMode } from "@oura/newscan/flags";
import { registerOnboardingSide, SAMPLE_SCAN_MS, type FirstCheckRun, type PitchRunDone, type SignedFacts } from "@oura/newscan/fixtures/journey";
import { daysAgoIso, JOURNEY_SAMPLES, journeySample, type JourneySample, type JourneyStage } from "@oura/newscan/fixtures/journeySamples";
import type { OnboardingPlanFacts } from "@oura/onboarding/derive";
import { missingAnswers } from "@oura/onboarding/setupQuestions";

const apiURL = import.meta.env.VITE_TALENT_AI_BE_BASE_URL;
const AEO_BASE = `${apiURL}/api/v1`;
// Its own flag, like the approval layer had: the rest of Oura is live on
// staging while these endpoints are being built.
const useFixtures = import.meta.env.VITE_ENABLE_OURA_ONBOARDING_LIVE !== "true";

/** True once the onboarding endpoints are live. */
export const isOnboardingLive = !useFixtures;
/** The sample store only ever runs outside production: no real user is shown a sample client. */
const sampleStoreOn = useFixtures && import.meta.env.VITE_APP_ENV !== "production";

function authHeaders(): Record<string, string> {
    const token = getCookie("authToken");
    return token ? { Authorization: `Bearer ${token}` } : {};
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface OnboardingPerson { name: string; email: string }
/** The person the form named for access. The form may give a name with no email. */
export interface AccessContact { name: string; email: string | null }

export type SiteBuilder = "wix" | "wordpress" | "framer" | "other";
/** The role we ask for on each platform, in the platform's own word. */
export const SITE_ROLE: Record<SiteBuilder, string> = {
    wix: "Website Manager",
    wordpress: "Administrator",
    framer: "Editor",
    other: "Can edit pages",
};

export type AccessKey = "gbp" | "website" | "gsc" | "ga4";
/**
 * not_started: nothing yet.
 * claimed:     the merchant says it is done; nobody has checked.
 * confirmed:   staff saw the invite and confirmed it by hand.
 * connected:   Oura tested the connection itself.
 */
export type AccessStatus = "not_started" | "claimed" | "confirmed" | "connected";
export interface AccessItem {
    key: AccessKey;
    status: AccessStatus;
    /** The role we hold, in plain words: "Manager", "Read only". */
    role: string;
    /** When access started. Null until confirmed or connected. */
    since: string | null;
    claimedAt: string | null;
    claimedBy: string | null;
    /** One line from the last check, such as "Connected to Aria Skin Clinic on Wix". */
    detail: string | null;
}

export type MessageKind = "kickoff" | "form_reminder" | "access_request" | "access_reminder";
export interface MessageDraft {
    to: OnboardingPerson[];
    cc: OnboardingPerson[];
    subject: string;
    body: string;
}
/**
 * The kick-off goes out as a SendGrid template with two variables, so staff
 * set who gets it and nothing else: there is no subject or body to edit or to
 * send (#4155).
 */
export type KickoffDraft = Pick<MessageDraft, "to" | "cc">;
/** The SendGrid template the kick-off is rendered from. */
export const KICKOFF_TEMPLATE_ID = "d-109cfc94b8214a9ab6763a32041bd17c";
/**
 * The access request goes out as a SendGrid template too (variable `first_name`,
 * ourteam-backend#5813), so staff choose who it goes to and nothing else (#4261).
 */
export const ACCESS_REQUEST_TEMPLATE_ID = "d-5e77147e08674452acbff7e0727ff5e0";
/**
 * `subject` and `body` are absent on a message sent as a template (the kick-off,
 * and now the access request): the words live in SendGrid, so there is no free
 * text to show. An access request sent before that change still carries them.
 */
export interface SentMessage extends KickoffDraft {
    subject?: string;
    body?: string;
    id: string;
    /** `account_ready`: "Your Oura account is ready", the one-off tour (backend #6473). */
    kind: MessageKind | "login_invite" | "account_ready";
    sentAt: string;
    sentBy: string;
}

export interface FormAnswers {
    contactName: string;
    contactEmail: string;
    outletAreas: string[];
    services: string[];
    goals: string[];
    plans: string;
    customerQuestions: string[];
    accessContact: AccessContact | null;
    approver: string;
    /** Where we send anything that needs the approver's yes. Absent from answers filed before it was asked. */
    approverEmail?: string;
    /** `url` opens the file. Null when the server has not stored it yet. */
    files: Array<{ name: string; size: number; url: string | null }>;
    other: string;
}
export interface OnboardingForm {
    /** The Google Form link with this client's website filled in. */
    link: string;
    answeredAt: string | null;
    answers: FormAnswers | null;
    /** How many items the answers added to Knowledge. Null until filed. */
    knowledgeItems: number | null;
    /** One setup: what the merchant has typed so far on their Setup page. The sample store's own. */
    draft?: SetupAnswers | null;
}

/**
 * The questions about their business, answered on the merchant's own Setup
 * page instead of the Google Form (one setup). The keys are FormAnswers'
 * own, so an answer given here files exactly as the form's would: who to
 * talk to comes from the login, and who looks after access from the steps
 * they send on.
 */
export interface SetupAnswers {
    outletAreas: string[];
    services: string[];
    customerQuestions: string[];
    goals: string[];
    plans: string;
    approver: string;
    /**
     * The approver's email, asked beside their name: we send them anything that
     * needs a yes. Absent: this backend does not take it yet, so the page asks
     * for the name only, as before. The server sends `""` once it does.
     */
    approverEmail?: string;
    other: string;
}
export type SetupAnswerKey = keyof SetupAnswers;

/** A step on the merchant's Setup page: the questions, or one access. */
export type SetupStepKey = "form" | AccessKey;

/**
 * The questions as the merchant's Setup page reads them.
 *
 * `inPlatform` true: the questions are answered on the page, from `draft`.
 * Absent or false: an older backend, and the Google Form at `link` is still
 * where they are answered, so the page links to it rather than ask twice.
 */
export interface MerchantForm {
    link: string | null;
    answeredAt: string | null;
    inPlatform?: boolean;
    /** The answers so far: what they typed, and what Oura filled in before they came. */
    draft?: SetupAnswers | null;
    /** The answers still as Oura filled them in, from the website, the Google listing or the pre-sale scan. A key leaves once the merchant changes it. */
    prefilled?: SetupAnswerKey[];
    /** When the draft was last saved. */
    savedAt?: string | null;
    /**
     * The answers as filed, however they came in: the Setup page or the
     * Google Form (the record's `form_answers`). Null until answered.
     * Absent: an older backend that does not send them yet, where `draft` is
     * the answers only if they were sent from the page (`savedAt` set); for a
     * Google Form client it is only Oura's pre-fill. See `sentAnswers`.
     */
    answers?: Partial<SetupAnswers> | null;
    /**
     * Staff viewing as the merchant may fill in and send these answers for
     * them (backend #6311), silently: nothing is emailed. True only for a
     * view-as session where the backend allows it; absent or false, the form
     * stays read-only there (#4497).
     */
    staffMayAnswer?: boolean;
}

export type ScanStatus = "not_started" | "running" | "complete" | "failed";

/**
 * The First AI check's draft (JOURNEY-SPEC §4, O6): the 50 Oura drafted when
 * the form landed, reviewed before the paid start.
 */
export interface OnboardingScanDraft {
    id: string;
    status: ScanDraftStatus;
    /** Cards still to answer before Approve (blockers and decides). 0 unless the draft is ready. */
    openDecisions: number;
    /** Who approved the 50, or the BD whose pitch approval carried. Null until approved. */
    approvedBy: string | null;
    approvedAt: string | null;
    purpose: ScanDraftPurpose | null;
    /** The draft carries a platform pitch's 50. Absent from an older backend: read as no pitch. */
    fromPitch?: boolean;
}

export interface OnboardingScan {
    status: ScanStatus;
    assessmentId: string | null;
    startedAt: string | null;
    finishedAt: string | null;
    /** 0 to 100 while running. Null when the server does not report one. */
    percent: number | null;
    /** The server's own sentence when the scan failed. */
    failure: string | null;
    /**
     * The first check's draft. Null: none yet (form not answered, Online, or
     * an older backend). Absent on a payload from before the joined journey.
     */
    draft?: OnboardingScanDraft | null;
    /** `brand`: an Online client's first check waits for brand checks to go live (O9). Null otherwise. */
    waitingFor?: "brand" | null;
}

/**
 * `send_failed` is ourteam-backend#5411 BE-1: a login reads "sent" only when an
 * email actually went. Before it, a send that failed still said sent, so the
 * screen told staff to wait for a client who had been told nothing (#4025 FE-3).
 *
 * `sending` is ourteam-backend#5523: the login email is still on its way (a
 * few seconds). It is neither a failure nor a reason to send again, so the
 * screen offers no send while it reads `sending`, and reads the record again.
 */
export type LoginStatus = "not_sent" | "sending" | "sent" | "send_failed" | "signed_in";
const LOGIN_STATUSES: readonly string[] = ["not_sent", "sending", "sent", "send_failed", "signed_in"];
/**
 * Whether a payload's `login.status` is one this screen knows. The type cannot
 * promise it at runtime: a value added on the server before this screen learns
 * it must offer no send, so the callers test it rather than trust the union.
 */
export function isKnownLoginStatus(status: unknown): status is LoginStatus {
    return typeof status === "string" && LOGIN_STATUSES.includes(status);
}
export interface OnboardingLogin {
    status: LoginStatus;
    contact: OnboardingPerson | null;
    sentAt: string | null;
    /** The set-password link stops working then. */
    linkExpiresAt: string | null;
    signedInAt: string | null;
    workspaceUrl: string | null;
    /** The server's sentence when `status` is `send_failed`; null otherwise. Shown as written. */
    failureDetail?: string | null;
    /**
     * The login email as it will be sent, for staff to read BEFORE sending
     * (BE-2). Present while `status` is `not_sent`, `sending`, `sent` or
     * `send_failed`; null once `signed_in`. Absent on a payload stored before BE-2.
     *
     * READ-ONLY (`editable: false`), and the link in it is always the literal
     * `[sign-in link]` -- no token ever reaches the payload. So this is what
     * the email says, not a thing to edit and send.
     */
    draft?: MessageDraft & { editable: false } | null;
}

export interface OnboardingPlan {
    ref: string;
    planName: string;
    termMonths: number;
    outlets: number;
    startedAt: string | null;
    /**
     * Priority keywords the plan holds (engagement.tracking.priority_keywords),
     * as the first check's "For the plan" card reads them. Absent: unknown.
     */
    priorityKeywords?: string[];
    /** Outlet names the plan holds (engagement.outlet_names). Absent: unknown. */
    outletNames?: string[];
}

export interface Onboarding {
    businessId: string;
    businessName: string;
    domain: string;
    createdAt: string;
    createdBy: string;
    paidAt: string | null;
    plan: OnboardingPlan | null;
    contact: OnboardingPerson;
    /**
     * Where the outlets are, as known at signing: from the pre-sale scan, or
     * typed by BD. The form asks the client to confirm them.
     */
    outletAreas: string[];
    accessContact: AccessContact | null;
    /** Null until Oura has read the site and recognised the builder. */
    siteBuilder: SiteBuilder | null;
    /**
     * How customers buy (`onboarding.buy_mode`, O9): the pitch draft's answer,
     * else the Client signed sheet's. `online` releases the login before any
     * scan. Null or absent: not known.
     */
    buyMode?: BuyMode | null;
    accountManager: OnboardingPerson;
    form: OnboardingForm;
    scan: OnboardingScan;
    /**
     * Results and plan, sent together once the first AI check is done (Jack,
     * 20 Oct 2026): the Baseline Snapshot, made by Oura once staff approve the
     * check's results, and the execution timeline staff upload, which publishes
     * Oura's own schedule (nothing is read from the PDF). Then one email. `resultsSentAt` is when that email
     * went; null until then. Absent: a backend that does not track it.
     */
    resultsSentAt?: string | null;
    /**
     * Who last picked the First AI check by hand (#4773), and when. Absent or null: never picked, or the
     * check has changed since. Shown as "Set by Dana, 28 Oct" on the Steps card.
     */
    firstCheckSetBy?: { name: string; at: string } | null;
    /**
     * The internal inbox a test of the Results and plan email goes to
     * (backend #6265), e.g. "user-notif@ourteam.ai". Present only where the
     * backend can send a test; absent, no test link shows.
     */
    resultsTestTo?: string | null;
    /**
     * "Your Oura account is ready" (one-off tour, backend #6473), for a client
     * onboarded by hand before the onboarding flow: offered when `eligible` (the
     * retrofit marker), last sent `sentAt`, to `to` (the login, else the contact).
     * Absent from a backend before it, where nothing is offered.
     */
    accountReady?: {
        eligible: boolean;
        sentAt: string | null;
        to: OnboardingPerson | null;
        /** A send still marked on its way, since when; null when none is (backend #6475). */
        sendingSince?: string | null;
        /** Where "Send Internal Test" goes: our own inbox, never the client (backend #6473). */
        testTo?: string | null;
    };
    /**
     * The latest time staff sent the Results and plan email again (backend
     * #6250). Its presence, null included, says this backend can resend:
     * absent, the Results and plan step offers no "Send again" (#4469).
     */
    resultsResentAt?: string | null;
    /**
     * The execution timeline staff uploaded for the send. Uploading it publishes
     * Oura's schedule from the plan's start date; the PDF's dates are not read.
     * Null: not uploaded yet.
     */
    resultsPack?: {
        timelineAt: string | null;
        /**
         * The Timeline version Oura's plan follows, once Oura reads the file
         * (proposed, backend handoff 21 Oct 2026). Absent: today's upload,
         * which reads nothing from the PDF.
         */
        timelineVersion?: string | null;
    } | null;
    /**
     * Staff read the first AI check's results and approved them for publishing
     * (Jack, 20 Oct 2026). Only then does Oura make the Baseline Snapshot from
     * them. Null: not approved yet. Absent: not tracked.
     */
    resultsApprovedAt?: string | null;
    /** When Oura finished making the Baseline Snapshot from the approved results. Null: not ready yet. */
    snapshotReadyAt?: string | null;
    /** The Baseline Snapshot itself, to read in the drawer. Null until the backend gives its address. */
    snapshotUrl?: string | null;
    login: OnboardingLogin;
    access: AccessItem[];
    /** Every message sent, newest first. */
    sent: SentMessage[];
    /** Set when all access is held. */
    completedAt: string | null;
    /**
     * `one_setup`: the kick-off is the one Setup email. It carries the login,
     * and the questions and the access are answered together on the client's
     * Setup page, so there is no separate login or access request to send.
     * Absent or null: the form, then the login and the access request, as before.
     */
    flow?: "one_setup" | null;
    /**
     * When an old-flow record was moved onto one setup (backend #6234). A
     * kick-off or form reminder sent before it belongs to the old flow: it is
     * not the Setup email, so that step still reads not sent. Absent or null:
     * one setup from the start, or still the old flow.
     */
    flowSwitchedAt?: string | null;
    /**
     * The client's plan facts, read with the list (backend #6086), so the Businesses list
     * needs no `getOnboardingPlanFacts` read per client. Absent: not read, so the caller
     * falls back to that read (an older backend, or a failed batch). Null: no plan. Only
     * `GET /onboarding` carries it; `getOnboarding` does not.
     */
    planFacts?: OnboardingPlanFacts | null;
}

export interface MerchantOnboarding {
    /**
     * Staff viewing as the merchant may confirm the website and Google profile
     * access for them (backend #6325), silently: no email, no notification.
     * True only for a view-as session where the backend allows it; absent or
     * false, those steps stay read-only there (#4510).
     */
    staffMayClaimAccess?: boolean;
    /**
     * The access keys this build lets the client claim with "I've added you" (backend
     * #6328). Search Console and Analytics appear here only when the backend supports
     * it; absent, their "add us as a user" guide stays hidden.
     */
    claimableAccess?: AccessKey[];
    businessId: string;
    businessName: string;
    contactName: string;
    outlets: number;
    siteBuilder: SiteBuilder | null;
    accountManager: OnboardingPerson;
    accessContact: AccessContact | null;
    access: AccessItem[];
    /**
     * Who the merchant sent the steps to, newest first. `steps` names the ones
     * sent; absent, the person was sent all of them.
     */
    sharedWith: Array<OnboardingPerson & { at: string; steps?: SetupStepKey[] }>;
    /**
     * The questions about their business: where to answer them and when they
     * did (ourteam-backend#5792). Absent from an older backend.
     */
    form?: MerchantForm | null;
    /** `brand`: an Online client's first check waits for brand checks (O9), as the staff record's `scan.waitingFor`. */
    waitingFor?: "brand" | null;
    /**
     * The First AI check, as the merchant may see it: where it stands, never
     * its questions. Absent from a backend that does not send it yet: the
     * Setup page then leaves the row out rather than guess.
     */
    firstCheck?: MerchantFirstCheck | null;
    completedAt: string | null;
}

/**
 * `after_answers`: waits on the business questions. `preparing`: the 50 are
 * drafted or with our team, who check them before the paid run (a failed run
 * reads the same: it is with us again). `running`: started. `reviewing`:
 * finished, and our team is reading it before the baseline report goes.
 * `ready`: the baseline report is sent (or, where that is not tracked, finished).
 */
export interface MerchantFirstCheck {
    status: "after_answers" | "preparing" | "running" | "reviewing" | "ready";
    finishedAt: string | null;
}

export interface CreateClientInput {
    /** The signed quote, as the quotation script writes it, or as read from the signed PDF. */
    engagement: EngagementSeed;
    /** The signed quote PDF, kept with the plan. Null when the quote came as engagement.json. */
    quotePdf?: File | null;
    domain: string;
    contact: OnboardingPerson;
    /** Known at signing: the pitched outlets, else the quote's named areas. Never typed. */
    outletAreas: string[];
    /** The day the payment cleared, as YYYY-MM-DD. Null: signed, not paid yet; the kick-off is held (call 18). */
    paidOn: string | null;
    /** How customers buy, from the pitch or the Client signed sheet (O9). */
    buyMode?: BuyMode | null;
    /** An existing business that has just signed. Absent for a new one. */
    businessId?: string;
    /** Null creates the client and leaves the kick-off unsent. Recipients only: no subject or body. */
    kickoff: KickoffDraft | null;
}
export type CreateClientResult =
    | { ok: true; businessId: string; onboarding: Onboarding }
    | { ok: false; code: string | null; message: string; reasons?: string[] };

export interface ReleaseInput {
    /** Null leaves the login unsent. */
    login: OnboardingPerson | null;
    /** Null leaves the access request unsent. Recipients only: the email is a template (#4261). */
    accessRequest: KickoffDraft | null;
}
/** A refusal the staffer can act on comes back as an answer, never a throw. */
export type OnboardingResult =
    | { ok: true; onboarding: Onboarding }
    | { ok: false; code: string | null; message: string };

export type MerchantConnectPayload = Record<string, string>;
export type MerchantConnectResult =
    | { ok: true; kind: "connected"; onboarding: MerchantOnboarding }
    /** Google asks the merchant to approve on its own screen. */
    | { ok: true; kind: "redirect"; url: string }
    | { ok: false; message: string };

// ---------------------------------------------------------------------------
// Calls
// ---------------------------------------------------------------------------

async function get<T>(path: string): Promise<T> {
    const res = await axios.get(`${AEO_BASE}${path}`, { headers: authHeaders() });
    return res.data.data as T;
}
async function post<T>(path: string, body: unknown): Promise<T> {
    const res = await axios.post(`${AEO_BASE}${path}`, body, { headers: authHeaders() });
    return res.data.data as T;
}
async function patch_<T>(path: string, body: unknown): Promise<T> {
    const res = await axios.patch(`${AEO_BASE}${path}`, body, { headers: authHeaders() });
    return res.data.data as T;
}
const enc = encodeURIComponent;

/**
 * Why the server refused, as sentences. Its `errors` is `[code, ...reasons]`
 * (`_refuse` in the backend's oura_onboarding.py): the first entry is a
 * machine code, and each one after it is `field: sentence`. `message` only
 * counts them ("This engagement cannot be seeded: 1 problem(s)."), so a screen
 * that shows the message alone tells staff nothing they can act on (#3998).
 */
export function refusalReasons(errors: unknown): string[] {
    if (!Array.isArray(errors)) return [];
    return errors
        .slice(1)
        .filter((r): r is string => typeof r === "string" && r.trim() !== "")
        .map((r) => {
            // "engagement.pageUnits: the monthly ..." reads as a sentence without its field path.
            const said = r.replace(/^[A-Za-z0-9_.[\]]+:\s*/, "").trim();
            const sentence = said.charAt(0).toUpperCase() + said.slice(1);
            return /[.!?]$/.test(sentence) ? sentence : `${sentence}.`;
        });
}

function refusal(err: unknown, fallback: string, where: string): { ok: false; code: string | null; message: string; reasons: string[] } {
    const e = err as { response?: { data?: { message?: string; code?: string; errors?: unknown } } };
    const message = e.response?.data?.message;
    if (!message) catchErrorLog(`[oura] ${where} failed: ${err}`);
    return { ok: false, code: e.response?.data?.code ?? null, message: message || fallback, reasons: refusalReasons(e.response?.data?.errors) };
}

/* ------------------------------------------------------------------------- *
 * Form answers that match no client (#3977)
 * ------------------------------------------------------------------------- */

/**
 * A form answer the backend could not file on a client.
 *
 * The website field on the form is prefilled but EDITABLE, so a client who
 * types a different domain from the one on their Oura record has their answer
 * stored with no `business_id`. Nothing on any screen used to say so: the
 * client's own Onboarding tab kept reading "Waiting for them to answer the
 * form" while the answer sat in the table.
 *
 * `answers` is the form's own payload, the same `FormAnswers` an onboarding
 * record carries — the staging row's keys are exactly those eleven. It is
 * optional because a row stored before a field existed will not have it, and
 * nothing here depends on reading it: the three facts a staffer needs to
 * recognise an answer are who sent it, what they typed, and when.
 */
export interface UnmatchedAnswer {
    id: string;
    /** When the client submitted it, ISO. */
    submittedAt: string;
    /** When Oura stored it, ISO. The two differ when a submission is picked up late. */
    receivedAt?: string | null;
    respondentEmail: string | null;
    /** The website they TYPED, which is the reason this did not match. */
    website: string | null;
    answers?: FormAnswers | null;
    /** Set aside by staff (backend #6454): who, when and why. Null or absent while it waits. */
    dismissed?: AnswerDismissal | null;
}

/** Who dismissed an answer that matched no client, when, and why (backend #6454). */
export interface AnswerDismissal {
    at: string;
    by: string | null;
    reason: string | null;
}

export type DismissAnswerResult =
    | { ok: true; answer: UnmatchedAnswer }
    | { ok: false; code: string | null; message: string };

export type AssignAnswerResult =
    | { ok: true; onboarding: Onboarding }
    | { ok: false; code: string | null; message: string };

/**
 * The answers that match no client, newest first, and whether the read failed.
 *
 * NOT a bare list, and the `failed` flag is the whole point. It used to answer
 * `[]` on any failure, so the notice vanished exactly when the queue could not
 * be checked — recreating the silence this feature exists to end (Jack,
 * #3991). "Nothing is waiting" and "I could not look" are different facts and
 * a screen must be able to tell them apart.
 *
 * Still never throws: the Businesses list around it must keep working.
 */
export async function listUnmatchedAnswers(): Promise<{ rows: UnmatchedAnswer[]; failed: boolean }> {
    if (useFixtures) return { rows: await mock.unmatched(), failed: false };
    try {
        return { rows: await get<UnmatchedAnswer[]>("/onboarding/unmatched"), failed: false };
    } catch (err) {
        catchErrorLog(`[oura] listUnmatchedAnswers failed: ${err}`);
        return { rows: [], failed: true };
    }
}

/**
 * File an unmatched answer on a client. The server answers with that client's
 * onboarding record, so the caller can show the Form answered step as done
 * without a second read.
 *
 * `409 already_filed` is the one refusal worth its own words: someone else
 * filed it while this list was open, which is not an error the staffer caused.
 */
export async function assignUnmatchedAnswer(rowId: string, businessId: string): Promise<AssignAnswerResult> {
    if (useFixtures) return mock.assignUnmatched(rowId, businessId);
    try {
        return { ok: true, onboarding: await post<Onboarding>(`/onboarding/unmatched/${enc(rowId)}/assign`, { businessId }) };
    } catch (err) {
        const status = (err as { response?: { status?: number } }).response?.status;
        if (status === 409) {
            return { ok: false, code: "already_filed", message: "That answer is already filed on a client. Reload the list." };
        }
        return refusal(err, "The answer was not filed. Try again in a moment.", "assignUnmatchedAnswer");
    }
}

/**
 * Set aside an answer that matches no client (a test answer, a typo in the
 * website), with the reason staff give (backend #6454). The answer is kept on
 * the server and leaves the unmatched list; nothing is filed and no client is
 * touched. Refusals come back in the server's words: reason_required,
 * reason_too_long, already_filed, already_dismissed.
 */
export async function dismissUnmatchedAnswer(rowId: string, reason: string): Promise<DismissAnswerResult> {
    if (useFixtures) return mock.dismissUnmatched(rowId, reason);
    try {
        return { ok: true, answer: await post<UnmatchedAnswer>(`/onboarding/unmatched/${enc(rowId)}/dismiss`, { reason }) };
    } catch (err) {
        return refusal(err, "The answer was not dismissed. Try again in a moment.", "dismissUnmatchedAnswer");
    }
}

/* ------------------------------------------------------------------------- *
 * Correcting the client's contact (#3992)
 * ------------------------------------------------------------------------- */

/** Which field a refusal is about, so the message lands beside the input that caused it. */
export type ContactField = "name" | "email";

export type ContactPatch = { name?: string; email?: string };
export type ContactResult =
    | { ok: true; onboarding: Onboarding }
    | { ok: false; code: string | null; message: string; field: ContactField | null };

/**
 * The server names the field in its `errors` array, as
 * `"contact.email: not an email address"`. Read the field from there rather
 * than guessing it from the sentence, so a reworded message still lands next
 * to the right input.
 */
function contactField(err: unknown): ContactField | null {
    const list = (err as { response?: { data?: { errors?: unknown } } })?.response?.data?.errors;
    const entries = Array.isArray(list) ? list.filter((x): x is string => typeof x === "string") : [];
    for (const entry of entries) {
        if (entry.startsWith("contact.email")) return "email";
        if (entry.startsWith("contact.name")) return "name";
    }
    return null;
}

/**
 * Correct the client's main contact (BE #5261). Either field, or both; a field
 * left out keeps its value.
 *
 * The contact was set once, by New client, and nothing could change it — a
 * mistyped email sent the kick-off to an address the client does not use, and
 * the only way forward was to delete the client and start again.
 *
 * Staff only on the server too, so this is not the only guard.
 */
export async function updateOnboardingContact(businessId: string, patch: ContactPatch): Promise<ContactResult> {
    if (useFixtures) return mock.updateContact(businessId, patch);
    try {
        return { ok: true, onboarding: await patch_<Onboarding>(`/businesses/${enc(businessId)}/onboarding/contact`, patch) };
    } catch (err) {
        const r = refusal(err, "The contact did not change. Try again in a moment.", "updateOnboardingContact");
        return { ...r, field: contactField(err) };
    }
}

/** Every client with an onboarding record. Never throws: a failed read is an empty list. */
export async function listOnboardings(): Promise<Onboarding[]> {
    if (useFixtures) return mock.list();
    try {
        return await get<Onboarding[]>("/onboarding");
    } catch (err) {
        catchErrorLog(`[oura] listOnboardings failed: ${err}`);
        return [];
    }
}

/** Null means this business has no onboarding record, which is an answer and not a failure. */
export async function getOnboarding(businessId: string): Promise<Onboarding | null> {
    if (useFixtures) return mock.get(businessId);
    try {
        return await get<Onboarding>(`/businesses/${enc(businessId)}/onboarding`);
    } catch (err) {
        const status = (err as { response?: { status?: number } }).response?.status;
        if (status === 404) return null;
        throw err;
    }
}

/** The form link, with the website and the outlet areas filled in. */
export const getFormLink = (domain: string, outletAreas: string[]): Promise<{ link: string }> =>
    useFixtures
        ? mock.formLink(domain, outletAreas)
        : get<{ link: string }>(`/onboarding/form-link?domain=${enc(domain)}&areas=${enc(outletAreas.join(", "))}`);

/**
 * What Oura already knows about a website before it signs: the outlet areas
 * the pre-sale scan ran on, and, when the pitch was drafted on the platform,
 * how customers buy and when it was pitched. Never throws: nothing known is
 * an empty list.
 */
export interface OnboardingPrefill {
    outletAreas: string[];
    /** The pitch draft's buy mode. Absent: no platform pitch, or an older backend. */
    buyMode?: BuyMode | null;
    /** When the pitch scan ran. */
    pitchedAt?: string | null;
}
export async function getOnboardingPrefill(domain: string): Promise<OnboardingPrefill> {
    if (useFixtures) return mock.prefill(domain);
    try {
        return await get<OnboardingPrefill>(`/onboarding/prefill?domain=${enc(domain)}`);
    } catch (err) {
        catchErrorLog(`[oura] getOnboardingPrefill failed: ${err}`);
        return { outletAreas: [] };
    }
}

export async function createClient(input: CreateClientInput): Promise<CreateClientResult> {
    if (useFixtures) return mock.create(input);
    try {
        const { quotePdf, ...payload } = input;
        // With the signed PDF the request is a form: the JSON body, and the file beside it.
        let body: unknown = payload;
        if (quotePdf) {
            const form = new FormData();
            form.append("payload", JSON.stringify(payload));
            form.append("quote", quotePdf, quotePdf.name);
            body = form;
        }
        const data = await post<{ businessId: string; onboarding: Onboarding }>("/onboarding/clients", body);
        return { ok: true, ...data };
    } catch (err) {
        return refusal(err, "The client was not created. Nothing was sent.", "createClient");
    }
}

export async function sendOnboardingMessage(businessId: string, kind: MessageKind, draft: MessageDraft | KickoffDraft): Promise<OnboardingResult> {
    if (useFixtures) return mock.send(businessId, kind, draft);
    try {
        // The kick-off and the access request are templates: only who they go to
        // is sent (#4155, #4261). The backend ignores a subject or body anyway.
        const payload = kind === "kickoff" || kind === "access_request" ? { kind, to: draft.to, cc: draft.cc } : { kind, ...draft };
        return { ok: true, onboarding: await post<Onboarding>(`/businesses/${enc(businessId)}/onboarding/messages`, payload) };
    } catch (err) {
        return refusal(err, "The email was not sent. Try again in a moment.", `sendOnboardingMessage ${kind}`);
    }
}

/**
 * Send the login email again (BE-1). No body: the server reads the contact as
 * it stands and mints a new link.
 *
 * Each resend STOPS THE EARLIER LINK WORKING and adds a row to `sent`, so this
 * is not a retry that can be pressed idly -- a client holding the old email
 * finds a dead link. The screen says so before it is pressed.
 *
 * Refusals: `no_login` (nothing to resend), `already_signed_in` (there is
 * nothing left to send), `login_send_failed` (the send itself failed, and the
 * record now says `send_failed`).
 */
export async function resendOnboardingLogin(businessId: string): Promise<OnboardingResult> {
    if (useFixtures) return mock.resendLogin(businessId);
    try {
        return { ok: true, onboarding: await post<Onboarding>(`/businesses/${enc(businessId)}/onboarding/login/resend`, {}) };
    } catch (err) {
        return refusal(err, "The login email was not sent again. Try again in a moment.", "resendOnboardingLogin");
    }
}

/** Sends the login and the access request together. Either half may be left out. */
export async function releaseOnboarding(businessId: string, input: ReleaseInput): Promise<OnboardingResult> {
    if (useFixtures) return mock.release(businessId, input);
    try {
        return { ok: true, onboarding: await post<Onboarding>(`/businesses/${enc(businessId)}/onboarding/release`, input) };
    } catch (err) {
        return refusal(err, "Nothing was sent. Try again in a moment.", "releaseOnboarding");
    }
}

export async function confirmAccess(businessId: string, key: AccessKey, confirmed: boolean): Promise<OnboardingResult> {
    if (useFixtures) return mock.confirm(businessId, key, confirmed);
    try {
        const verb = confirmed ? "confirm" : "unconfirm";
        return { ok: true, onboarding: await post<Onboarding>(`/businesses/${enc(businessId)}/onboarding/access/${key}/${verb}`, {}) };
    } catch (err) {
        return refusal(err, "That was not saved. Try again in a moment.", `confirmAccess ${key}`);
    }
}

/** What the first AI check runs on. Staff check both before they start it. */
export interface ScanInputs { services: string[]; outletAreas: string[] }

/** The first AI check starts on staff's go, never by itself (YW, 30 Sep 2026). */
export async function startFirstScan(businessId: string, inputs: ScanInputs): Promise<OnboardingResult> {
    if (useFixtures) return mock.startScan(businessId, inputs);
    try {
        return { ok: true, onboarding: await post<Onboarding>(`/businesses/${enc(businessId)}/onboarding/scan/start`, inputs) };
    } catch (err) {
        return refusal(err, "The AI check did not start. Try again in a moment.", "startFirstScan");
    }
}

/**
 * Record the payment for a client signed before it cleared: writes `paid_on`
 * and the plan start. The held kick-off is then the Next card's to send.
 */
export async function recordOnboardingPayment(businessId: string, paidOn: string): Promise<OnboardingResult> {
    if (useFixtures) return mock.recordPayment(businessId, paidOn);
    try {
        return { ok: true, onboarding: await post<Onboarding>(`/businesses/${enc(businessId)}/onboarding/payment`, { paidOn }) };
    } catch (err) {
        return refusal(err, "The payment was not recorded. Try again in a moment.", "recordOnboardingPayment");
    }
}

/**
 * What the plan holds once the first check is done: the signature pair and
 * whether the schedule was ever published. Null only when the business has no
 * plan. A read that FAILED rejects: a null here tells the Next card there is no
 * signature or publish work left, so an outage must not read as one (Jack, #4291).
 * Callers show a retryable error instead.
 *
 * Live reads the engagement and the timeline. `needs_person` needs O4 on the
 * backend (`tracking.signature_pick`, with the closest question); until then a
 * signature that is not stored reads `pending` and no card asks for it.
 */
export async function getOnboardingPlanFacts(businessId: string): Promise<OnboardingPlanFacts | null> {
    if (useFixtures) return mock.planFacts(businessId);
    try {
        const [plan, timeline] = await Promise.all([getEngagementIfExists(businessId), getTimeline(businessId)]);
        if (!plan) return null;
        const sig = plan.tracking?.signature;
        const pick = plan.tracking?.signature_pick as { status?: string; closest?: { n?: number; reason?: string } | null } | undefined;
        const closest = pick?.closest && typeof pick.closest.n === "number" ? { n: pick.closest.n, reason: pick.closest.reason ?? "" } : null;
        return {
            signature: sig?.visibility && sig?.coverage ? "held" : pick?.status === "needs_person" ? "needs_person" : "pending",
            closest,
            published: !!timeline?.published,
            // The Timeline's own readiness, read the same way its publish button reads it.
            canPublish: !!timeline && timelinePublishable(timeline),
            blockers: timeline ? publishBlockers(timeline) : [],
        };
    } catch (err) {
        catchErrorLog(`[oura] getOnboardingPlanFacts failed: ${err}`);
        throw err;
    }
}

/** Staff take the question Oura found closest as the one the client wins (O4). */
export async function chooseSignatureQuestion(businessId: string, question: number): Promise<OnboardingResult> {
    if (useFixtures) return mock.chooseSignature(businessId, question);
    try {
        return { ok: true, onboarding: await post<Onboarding>(`/businesses/${enc(businessId)}/onboarding/signature`, { question }) };
    } catch (err) {
        return refusal(err, "The question was not saved. Try again in a moment.", "chooseSignatureQuestion");
    }
}

/** The scans of a business's domain, newest first, for "Use another scan" (#4773). The caller keeps the complete ones of its own base_path. */
export type FirstCheckScansResult = { ok: true; scans: AeoAssessment[] } | { ok: false; message: string };

/**
 * The most `GET /assessments` returns in one call (its `limit` caps at 100, and it has no page or offset).
 * With `base_path` sent (ourteam-backend#6659) the 100 are this site's; on an older backend the param is
 * ignored and the 100 are the domain's, which is why the caller filters on base_path again.
 */
export const FIRST_CHECK_SCAN_LIMIT = 100;

/** `basePath` is the business's own: "" for the site root, "/name" for a site under a path. */
export async function listFirstCheckScans(businessId: string, domain: string, basePath: string): Promise<FirstCheckScansResult> {
    if (useFixtures) return mock.firstCheckScans(businessId, basePath);
    try {
        const res = await axios.get(`${AEO_BASE}/assessments`, {
            headers: authHeaders(),
            params: { domain, status: "complete", limit: FIRST_CHECK_SCAN_LIMIT, base_path: basePath },
        });
        const body = res.data?.data ?? res.data;
        return { ok: true, scans: Array.isArray(body) ? body : [] };
    } catch (err) {
        catchErrorLog(`[oura] listFirstCheckScans failed: ${err}`);
        return { ok: false, message: "The scans could not be read. Try again in a moment." };
    }
}

/**
 * Staff name a completed scan of this business as its First AI check (#4773). A refusal carries the
 * server's code, which is `errors[0]` (the response's `code` is the HTTP status), and nothing changed.
 */
export async function chooseFirstCheck(businessId: string, assessmentId: string): Promise<OnboardingResult> {
    if (useFixtures) return mock.chooseFirstCheck(businessId, assessmentId);
    try {
        return { ok: true, onboarding: await post<Onboarding>(`/businesses/${enc(businessId)}/onboarding/first-check`, { assessmentId }) };
    } catch (err) {
        const e = err as { response?: { data?: { message?: string; errors?: unknown } } };
        const errors: unknown = e.response?.data?.errors;
        const code = Array.isArray(errors) && typeof errors[0] === "string" ? errors[0] : null;
        const message = e.response?.data?.message;
        if (!message) catchErrorLog(`[oura] chooseFirstCheck failed: ${err}`);
        return { ok: false, code, message: message || "The First AI check was not changed. Try again in a moment." };
    }
}

/** The signed-in merchant's own set-up. Null when their business has no onboarding record. */
export async function getMyOnboarding(): Promise<MerchantOnboarding | null> {
    if (useFixtures) return mock.mine();
    try {
        return await get<MerchantOnboarding>("/businesses/mine/onboarding");
    } catch (err) {
        const status = (err as { response?: { status?: number } }).response?.status;
        if (status === 404) return null;
        throw err;
    }
}

/** `platform` is what the merchant said their website runs on, sent with the website claim. */
export async function claimMyAccess(key: AccessKey, claimed: boolean, platform?: SiteBuilder): Promise<MerchantOnboarding> {
    if (useFixtures) return mock.claim(key, claimed, platform);
    return post<MerchantOnboarding>(`/businesses/mine/onboarding/access/${key}/${claimed ? "claim" : "unclaim"}`, platform ? { platform } : {});
}

export async function connectMyAccess(key: AccessKey, payload: MerchantConnectPayload): Promise<MerchantConnectResult> {
    if (useFixtures) return mock.connect(key, payload);
    try {
        const data = await post<{ redirectUrl?: string; onboarding?: MerchantOnboarding }>(`/businesses/mine/onboarding/access/${key}/connect`, payload);
        if (data.redirectUrl) return { ok: true, kind: "redirect", url: data.redirectUrl };
        if (data.onboarding) return { ok: true, kind: "connected", onboarding: data.onboarding };
        return { ok: false, message: "The connection did not finish. Try again in a moment." };
    } catch (err) {
        return { ok: false, message: refusal(err, "We could not connect. Check the details and try again.", `connectMyAccess ${key}`).message };
    }
}

export async function disconnectMyAccess(key: AccessKey): Promise<MerchantOnboarding> {
    if (useFixtures) return mock.disconnect(key);
    return post<MerchantOnboarding>(`/businesses/mine/onboarding/access/${key}/disconnect`, {});
}

/** `steps` sends only those; absent sends every step still open. */
export async function shareMySteps(person: OnboardingPerson, steps?: SetupStepKey[]): Promise<MerchantOnboarding> {
    if (useFixtures) return mock.share(person, steps);
    return post<MerchantOnboarding>("/businesses/mine/onboarding/share-steps", steps ? { ...person, steps } : person);
}

/** Results and plan: staff approve the first check's results; Oura then makes the Baseline Snapshot. */
export async function approveResults(businessId: string): Promise<void> {
    if (useFixtures) { onboardingSim.resultsApproved(businessId); return; }
    await post(`/businesses/${encodeURIComponent(businessId)}/onboarding/approve-results`, {});
}

/**
 * Results and plan: the upload just made was the execution timeline. The
 * backend marks it and publishes Oura's schedule from the plan's start date.
 * It does not read the PDF's dates, so the two match only on the same start.
 */
export async function markTimelineUploaded(businessId: string): Promise<void> {
    if (useFixtures) { onboardingSim.timelineUploaded(businessId); return; }
    await post(`/businesses/${encodeURIComponent(businessId)}/onboarding/execution-timeline`, {});
}

/** Results and plan: send the one email with both documents. The merchant's results open with it. */
/** Send the Results and plan email again, unchanged (backend #6250). Nothing else moves. */
export async function resendResults(businessId: string): Promise<{ ok: true } | ReturnType<typeof refusal>> {
    if (useFixtures) return { ok: true };
    try {
        await post(`/businesses/${encodeURIComponent(businessId)}/onboarding/send-results/resend`, {});
        return { ok: true };
    } catch (err) {
        return refusal(err, "The email did not go out again. Try again in a moment.", "resendResults");
    }
}

/**
 * "Your Oura account is ready" (one-off tour, backend #6473) to a client onboarded
 * by hand: its login and the way to its Setup page. The server answers the record;
 * the caller re-reads. Refusals in the server's words (not_retrofitted, no_contact,
 * send_failed).
 */
export async function sendAccountReady(
    businessId: string,
    opts: { resendUnknown?: boolean } = {},
): Promise<{ ok: true } | (ReturnType<typeof refusal> & { unconfirmed: boolean })> {
    if (useFixtures) return { ok: true };
    try {
        await post(`/businesses/${encodeURIComponent(businessId)}/onboarding/account-ready`, opts.resendUnknown ? { resendUnknown: true } : {});
        return { ok: true };
    } catch (err) {
        // `send_unconfirmed` (backend #6475): an earlier send did not finish and it is not known
        // whether it arrived. Staff may check the client's inbox, then send again explicitly.
        const errors = (err as { response?: { data?: { errors?: unknown } } }).response?.data?.errors;
        const unconfirmed = Array.isArray(errors) && errors[0] === "send_unconfirmed";
        return { ...refusal(err, "The email did not go out. Try again in a moment.", "sendAccountReady"), unconfirmed };
    }
}

/**
 * The account-ready email exactly as the client would get it, sent only to the internal
 * test inbox, with a link that logs nobody in (backend #6473). Changes nothing.
 */
export async function sendAccountReadyTest(businessId: string): Promise<{ ok: true } | ReturnType<typeof refusal>> {
    if (useFixtures) return { ok: true };
    try {
        await post(`/businesses/${encodeURIComponent(businessId)}/onboarding/account-ready/test`, {});
        return { ok: true };
    } catch (err) {
        return refusal(err, "The test email did not go out. Try again in a moment.", "sendAccountReadyTest");
    }
}

/** The Results and plan email, sent only to the internal test inbox (backend #6265). Changes nothing. */
export async function sendResultsTest(businessId: string): Promise<{ ok: true } | ReturnType<typeof refusal>> {
    if (useFixtures) return { ok: true };
    try {
        await post(`/businesses/${encodeURIComponent(businessId)}/onboarding/send-results/test`, {});
        return { ok: true };
    } catch (err) {
        return refusal(err, "The test email did not go out. Try again in a moment.", "sendResultsTest");
    }
}

export async function sendResults(businessId: string): Promise<void> {
    if (useFixtures) { onboardingSim.resultsSent(businessId); return; }
    await post(`/businesses/${encodeURIComponent(businessId)}/onboarding/send-results`, {});
}

/** One setup: keep what the merchant has typed so far. Only the keys given change. */
export async function saveMyAnswers(answers: Partial<SetupAnswers>): Promise<MerchantOnboarding> {
    if (useFixtures) return mock.saveAnswers(answers);
    return patch_<MerchantOnboarding>("/businesses/mine/onboarding/form", { answers });
}

export type SubmitAnswersResult =
    | { ok: true; onboarding: MerchantOnboarding }
    /** `fields`: the server's sentence per answer it refused, by key. */
    | { ok: false; message: string; fields: Partial<Record<SetupAnswerKey, string>> };

/**
 * One setup: send the answers in. They file on the record as the Google Form's do.
 * Its own route, `POST .../onboarding/form/submit`, not the save's
 * `PATCH .../onboarding/form`: the save is partial and frequent, the send
 * checks every required answer and files them. The backend handoff names both.
 */
export async function submitMyAnswers(answers: SetupAnswers): Promise<SubmitAnswersResult> {
    if (useFixtures) return mock.submitAnswers(answers);
    try {
        return { ok: true, onboarding: await post<MerchantOnboarding>("/businesses/mine/onboarding/form/submit", { answers }) };
    } catch (err) {
        const e = err as { response?: { data?: { fields?: Partial<Record<SetupAnswerKey, string>> } } };
        return { ok: false, message: refusal(err, "Your answers were not sent. Try again in a moment.", "submitMyAnswers").message, fields: e.response?.data?.fields ?? {} };
    }
}

// ---------------------------------------------------------------------------
// In-memory store, persisted to localStorage. DELETE once the endpoints exist.
// ---------------------------------------------------------------------------

interface Store {
    records: Record<string, Onboarding>;
    /** Businesses this store created, so the list and the detail can find them. */
    businesses: AeoBusiness[];
    shared: Record<string, Array<OnboardingPerson & { at: string; steps?: SetupStepKey[] }>>;
    /** Form answers that match no client (#3977). Absent in a store saved before they existed. */
    unmatched?: UnmatchedAnswer[];
    /** Joined-journey samples already seeded, so a rewound one is not seeded again. */
    journeySeeded?: string[];
    /** The plan's signature and schedule, per business, once its first check is done. */
    planFacts?: Record<string, OnboardingPlanFacts>;
    seq: number;
}

const KEY = "__oura_onboarding_store__";
/** The signed-in merchant in the demo. The same business clientReview.ts uses. */
const DEMO_MERCHANT_BUSINESS = "nyonya-table";
const ANGEL: OnboardingPerson = { name: "Angel", email: "oura@ourteam.ai" };
/** How long the sample AI check takes: the one sample clock every scan runs on, the pitch included. */
const MOCK_SCAN_MS = SAMPLE_SCAN_MS;
const DAY = 86_400_000;

const ACCESS_ROLE: Record<AccessKey, string> = {
    gbp: "Manager",
    website: SITE_ROLE.other,
    gsc: "Read only",
    ga4: "Read only",
};

function emptyStore(): Store {
    return { records: {}, businesses: [], shared: {}, unmatched: [SAMPLE_UNMATCHED], journeySeeded: [], seq: 0 };
}

/**
 * One unmatched answer in the sample store, so the notice, the list and the
 * assign are reachable on fixtures. It mirrors the real one found on staging:
 * a client who typed a website of their own instead of the one on their record.
 */
const SAMPLE_UNMATCHED: UnmatchedAnswer = {
    id: "unmatched-1",
    submittedAt: new Date(Date.now() - 2 * 3_600_000).toISOString(),
    receivedAt: new Date(Date.now() - 2 * 3_600_000).toISOString(),
    respondentEmail: "ops@blancdental.com.my",
    website: "blancdental.com.my",
    answers: {
        contactName: "Jovi Chu",
        contactEmail: "ops@blancdental.com.my",
        outletAreas: [],
        services: ["Invisalign clear aligners", "Professional teeth whitening"],
        goals: ["Increase bookings or enquiries"],
        plans: "",
        customerQuestions: [],
        accessContact: null,
        approver: "",
        files: [],
        other: "",
    },
};
function load(): Store {
    try {
        const raw = window.localStorage.getItem(KEY);
        if (raw) return { ...emptyStore(), ...(JSON.parse(raw) as Store) };
    } catch { /* private mode, or a stale shape: start clean */ }
    return emptyStore();
}
/** Riverside's sample dates, before its 7 Jul scan (the one the business page shows). */
const RIVERSIDE_DATES = {
    signed: "2026-07-01T02:00:00Z",
    answered: "2026-07-06T03:00:00Z",
    scanStarted: "2026-07-07T03:40:00Z",
    baseline: "2026-07-07T04:00:00Z",
} as const;

/**
 * A store seeded before the dates above said Riverside's baseline was the day
 * it was seeded, while its business page shows the 7 Jul scan. Moved once.
 */
function alignRiverside(o: Onboarding | undefined): void {
    if (!o || o.scan.assessmentId !== "new-clinic-latest" || !o.scan.finishedAt || o.scan.finishedAt <= RIVERSIDE_DATES.baseline) return;
    o.scan = { ...o.scan, startedAt: RIVERSIDE_DATES.scanStarted, finishedAt: RIVERSIDE_DATES.baseline };
    if (o.createdAt > RIVERSIDE_DATES.signed) o.createdAt = RIVERSIDE_DATES.signed;
    if (o.form.answeredAt && o.form.answeredAt > RIVERSIDE_DATES.answered) o.form.answeredAt = RIVERSIDE_DATES.answered;
    o.sent = o.sent.map((m) => (m.kind === "kickoff" && m.sentAt > RIVERSIDE_DATES.signed ? { ...m, sentAt: RIVERSIDE_DATES.signed } : m));
}

let store: Store = typeof window === "undefined" || !sampleStoreOn ? emptyStore() : load();
alignRiverside(store.records["new-clinic"]);

function save() {
    if (!sampleStoreOn) return;
    try { window.localStorage.setItem(KEY, JSON.stringify(store)); } catch { /* ignore */ }
}
const nowIso = () => new Date().toISOString();
const ago = (days: number, hours = 0) => new Date(Date.now() - days * DAY - hours * 3_600_000).toISOString();
const nextId = (p: string) => `${p}_${++store.seq}`;
function delay<T>(v: T, ms = 180): Promise<T> {
    save();
    return new Promise((r) => window.setTimeout(() => r(JSON.parse(JSON.stringify(v)) as T), ms));
}

/**
 * The short link the client reads in the email. The server keeps the long
 * prefilled Google Form link behind it (handoff B1), because a link of
 * encoded question ids is the least polished line a client sees from us.
 */
function formLinkFor(domain: string, areas: string[] = []): string {
    let h = 0;
    for (const c of `${domain}|${areas.join(",")}`) h = (h * 31 + c.charCodeAt(0)) >>> 0;
    return `https://ourteam.ai/f/${h.toString(36).slice(0, 6)}`;
}

/** What the sample pre-sale scans ran on. */
const PRESALE_AREAS: Record<string, string[]> = {
    "riversidedental.co": ["Petaling Jaya"],
    "thepilatescollective.my": ["Bangsar"],
    "nyonyatable.com": ["Bukit Bintang"],
};
function freshAccess(): AccessItem[] {
    return (["gbp", "website", "gsc", "ga4"] as AccessKey[]).map((key) => ({
        key, status: "not_started", role: ACCESS_ROLE[key], since: null, claimedAt: null, claimedBy: null, detail: null,
    }));
}
function freshRecord(b: { id: string; name: string; domain: string }, contact: OnboardingPerson, plan: OnboardingPlan | null, createdAt: string): Onboarding {
    return {
        businessId: b.id, businessName: b.name, domain: b.domain, createdAt, createdBy: "Dana", paidAt: createdAt,
        plan, contact, outletAreas: [], accessContact: null, siteBuilder: null, accountManager: ANGEL,
        form: { link: formLinkFor(b.domain), answeredAt: null, answers: null, knowledgeItems: null },
        scan: { status: "not_started", assessmentId: null, startedAt: null, finishedAt: null, percent: null, failure: null },
        login: { status: "not_sent", contact: null, sentAt: null, linkExpiresAt: null, signedInAt: null, workspaceUrl: null },
        access: freshAccess(), sent: [], completedAt: null,
    };
}
/** Oura's own invite, as the record shows it. The server fills the link in. */
/** "Mei" for "Dr Mei Tan": the greeting uses the first name, as the drafts do. */
const greetName = (name: string) => {
    const words = name.trim().split(/\s+/).filter(Boolean);
    const rest = /^(dr\.?|datin|datuk|dato'?|mr|mrs|ms|puan|encik)$/i.test(words[0] ?? "") ? words.slice(1) : words;
    return rest[0] ?? name;
};
const LOGIN_INVITE_BODY = (name: string, online = false) =>
    `Hi ${name},\n\nYour Oura login is ready. ${online ? "Set your password to see your plan. Your first AI check runs when brand checks go live." : "Set your password to see your first AI check and your plan."}\n\n[Set your password]\n\nThis link lasts 7 days.\n\nAngel`;

function sentMessage(kind: SentMessage["kind"], draft: MessageDraft | KickoffDraft, sentAt: string): SentMessage {
    return { id: nextId("msg"), kind, ...draft, sentAt, sentBy: ANGEL.name };
}
/**
 * What a sample client of each vertical writes on the form, so a dental
 * clinic answers with dental services and dental questions (Angel, 15 Oct).
 */
const VERTICALS = {
    dental: {
        match: /dental|dentist|smile|teeth|tooth|invisalign|braces/,
        services: ["Scaling and polishing", "Teeth whitening", "Braces", "Invisalign"],
        questions: ["How much is scaling and polishing?", "Do you take walk-ins?", "Is parking easy?"],
        goals: ["More new patients", "Be found on Google Maps"],
    },
    spine: {
        match: /physio|chiro|spine|back|posture|rehab/,
        services: ["Back pain treatment", "Sports injury rehab", "Posture assessment"],
        questions: ["How much is a first visit?", "Does it hurt?", "Are you open on Saturdays?"],
        goals: ["More patients with back pain", "Be found on Google Maps"],
    },
    skin: {
        match: /skin|aesthetic|derma|laser|glow|beauty|facial/,
        services: ["Pico laser", "Skin booster", "Laser hair removal", "Acne facial"],
        questions: ["How much is Pico laser?", "Is there downtime?", "How many sessions do I need?"],
        goals: ["More Pico laser clients", "Be found on Google Maps"],
    },
    fitness: {
        match: /pilates|yoga|gym|fitness|studio|barre/,
        services: ["Reformer pilates", "Mat pilates", "Private sessions"],
        questions: ["How much is a trial class?", "Is it good for beginners?", "Is parking easy?"],
        goals: ["More trial classes", "Fill the morning classes"],
    },
    shop: {
        match: /\btea\b|tea |coffee|brew|matcha|shop|store|online/,
        services: ["Loose leaf tea", "Tea gift sets", "Matcha"],
        questions: ["Do you ship to Singapore?", "Which tea helps with sleep?", "Do you have gift wrapping?"],
        goals: ["More online orders", "More repeat customers"],
    },
    food: {
        match: /restaurant|cafe|food|kitchen|table|dining|bakery/,
        services: ["Set lunch", "Private dining", "Catering"],
        questions: ["Do you take reservations?", "Is there parking?", "Are you open on Sundays?"],
        goals: ["More weekday lunch guests", "More private dining bookings"],
    },
} as const;
type Vertical = keyof typeof VERTICALS;

/** The vertical a business's words point to; a clinic of no known kind reads as skin, the old sample. */
function verticalOf(...words: Array<string | null | undefined>): Vertical {
    const text = words.filter(Boolean).join(" ").toLowerCase();
    return (Object.keys(VERTICALS) as Vertical[]).find((k) => VERTICALS[k].match.test(text)) ?? "skin";
}

function sampleAnswers(contact: OnboardingPerson, areas: string[], services: string[], accessContact: AccessContact): FormAnswers {
    const v = VERTICALS[verticalOf(...services)];
    return {
        contactName: contact.name, contactEmail: contact.email, outletAreas: areas, services,
        goals: [...v.goals],
        plans: "A third outlet opens in January 2027.",
        customerQuestions: [...v.questions],
        accessContact, approver: contact.name,
        files: [{ name: "Price list 2026.pdf", size: 412_000, url: null }],
        other: "",
    };
}

/** The sample client the store adds to the Businesses list: signed, and no scan yet. */
const ARIA: AeoBusiness = {
    id: "ariaskin.example",
    domain: "ariaskin.example",
    business_name: "Aria Skin Clinic",
    category: "Aesthetic clinic",
    city: "Bangsar",
    state: "client",
    plan: null,
    created_at: ago(4),
    latest_assessment: null,
    engagement_status: "signed",
    timeline_published_version: null,
    // Its record's plan, as the list row carries it: signed, clock not started.
    lifecycle: { state: "signed", since: null },
    plan_name: "6-Month plan",
    plan_term_months: 6,
    plan_started_on: null,
    plan_ends_on: null,
    plan_month: null,
};

/**
 * 3 sample clients, one at each point where somebody has to act:
 *   Aria Skin Clinic   kick-off sent 4 days ago, form not answered
 *   Riverside Dental   form answered, AI check finished (7 Jul), nothing sent yet
 *   Nyonya Table       signed in, 2 of 4 access held, 1 waiting to be confirmed
 * Runs once, only when the store is empty. DELETE with the rest of the mock.
 */
function seedDemo() {
    if (Object.keys(store.records).length > 0) return;

    const aria = freshRecord({ id: ARIA.id, name: ARIA.business_name, domain: ARIA.domain }, { name: "Sarah Lim", email: "sarah@ariaskin.example" },
        { ref: "OT-2026-ARIA-001", planName: "6-Month plan", termMonths: 6, outlets: 2, startedAt: null }, ago(4));
    aria.sent.push(sentMessage("kickoff", { to: [aria.contact], cc: [] }, ago(4)));
    store.businesses = [ARIA];
    aria.outletAreas = ["Bangsar", "Mont Kiara"];
    aria.form.link = formLinkFor(aria.domain, aria.outletAreas);
    store.records[aria.businessId] = aria;

    // Riverside's first AI check IS its scan on the business page (new-clinic-latest, 7 Jul), so every
    // date sits before it: signed 1 Jul, form 6 Jul, baseline 7 Jul. One story on every screen.
    const river = freshRecord({ id: "new-clinic", name: "Riverside Dental", domain: "riversidedental.co" }, { name: "Dr Mei Tan", email: "mei@riversidedental.co" },
        { ref: "OT-2026-RIV-001", planName: "Annual plan", termMonths: 12, outlets: 1, startedAt: null }, RIVERSIDE_DATES.signed);
    river.sent.push(sentMessage("kickoff", { to: [river.contact], cc: [] }, RIVERSIDE_DATES.signed));
    river.accessContact = { name: "Daniel Tan", email: "daniel@webstudio.example" };
    river.siteBuilder = "wordpress";
    river.form.answeredAt = RIVERSIDE_DATES.answered;
    river.form.answers = sampleAnswers(river.contact, ["Petaling Jaya"], ["Invisalign", "Dental implants", "Veneers", "Wisdom tooth surgery"], river.accessContact);
    river.form.knowledgeItems = 4;
    river.scan = { status: "complete", assessmentId: "new-clinic-latest", startedAt: RIVERSIDE_DATES.scanStarted, finishedAt: RIVERSIDE_DATES.baseline, percent: 100, failure: null };
    river.outletAreas = ["Petaling Jaya"];
    river.form.link = formLinkFor(river.domain, river.outletAreas);
    store.records[river.businessId] = river;

    // The same quote the Plan page shows for this business (clientReview.ts).
    const nyonya = freshRecord({ id: DEMO_MERCHANT_BUSINESS, name: "Nyonya Table", domain: "nyonyatable.com" }, { name: "Siew Ling", email: "siewling@nyonyatable.com" },
        { ref: "OT-2026-NYO-001", planName: "Annual plan", termMonths: 12, outlets: 1, startedAt: "2026-08-18" }, "2026-08-14T02:10:00Z");
    nyonya.accessContact = { name: "Wei Jie", email: "weijie@nyonyatable.com" };
    nyonya.siteBuilder = "wordpress";
    nyonya.form.answeredAt = "2026-08-15T03:20:00Z";
    nyonya.form.answers = sampleAnswers(nyonya.contact, ["Bukit Bintang"], ["Set lunch", "Private dining", "Catering"], nyonya.accessContact);
    nyonya.form.knowledgeItems = 4;
    nyonya.scan = { status: "complete", assessmentId: "nyonya-table-latest", startedAt: "2026-08-15T03:21:00Z", finishedAt: "2026-08-15T03:34:00Z", percent: 100, failure: null };
    nyonya.login = { status: "signed_in", contact: nyonya.contact, sentAt: "2026-08-15T04:00:00Z", linkExpiresAt: "2026-08-22T04:00:00Z", signedInAt: "2026-08-16T01:30:00Z", workspaceUrl: "https://nyonya-table.staging.ourteam.ai" };
    nyonya.access = nyonya.access.map((a) => {
        if (a.key === "website") return { ...a, status: "confirmed", role: SITE_ROLE.wordpress, since: "2026-08-17T06:00:00Z", detail: "Confirmed by Angel" };
        if (a.key === "gsc") return { ...a, status: "connected", since: "2026-08-17T06:10:00Z", detail: "Reading nyonyatable.com" };
        if (a.key === "gbp") return { ...a, status: "claimed", claimedAt: ago(1), claimedBy: "Siew Ling" };
        return a;
    });
    nyonya.sent.push(
        sentMessage("access_reminder", {
            to: [{ name: "Wei Jie", email: "weijie@nyonyatable.com" }], cc: [nyonya.contact], subject: "2 left to set up",
            body: "Hi Wei Jie,\n\nStill to do: Google Business Profile, Google Analytics.\n\nThe steps for each one are on the Set-up page in Oura. No passwords needed.\n\nAngel",
        }, ago(2)),
        // A template now (#4261): who it went to, and no free text.
        sentMessage("access_request", {
            to: [{ name: "Wei Jie", email: "weijie@nyonyatable.com" }], cc: [nyonya.contact],
        }, "2026-08-15T04:00:00Z"),
        sentMessage("login_invite", { to: [nyonya.contact], cc: [], subject: "Your Oura login is ready", body: LOGIN_INVITE_BODY(nyonya.contact.name) }, "2026-08-15T04:00:00Z"),
        sentMessage("kickoff", { to: [nyonya.contact], cc: [] }, "2026-08-14T02:15:00Z"),
    );
    nyonya.outletAreas = ["Bukit Bintang"];
    nyonya.form.link = formLinkFor(nyonya.domain, nyonya.outletAreas);
    store.records[nyonya.businessId] = nyonya;
    save();
}

/* ---- Joined journey (JOURNEY-SPEC v1.1 §1): the samples both stores share ---- */

/** The form answers a journey sample gives, as fixtures/journeySamples.ts lists them. */
function journeyAnswers(j: JourneySample): FormAnswers {
    return {
        ...sampleAnswers(j.contact, j.form.areas, j.form.services, j.accessContact),
        customerQuestions: [...j.form.customerQuestions],
        goals: [...j.form.goals],
        plans: j.form.plans,
        other: j.form.other,
    };
}

/** The business row a journey sample has at a stage: a prospect with its pitch, or a signed client. */
function journeyRow(j: JourneySample, stage: JourneyStage): AeoBusiness {
    return {
        id: j.id,
        domain: j.domain,
        business_name: j.name,
        category: j.category,
        city: j.city,
        state: stage === "prospect" ? "prospect" : "client",
        plan: null,
        created_at: daysAgoIso((j.pitch?.daysAgo ?? j.signedDaysAgo) + 2),
        latest_assessment: j.pitch
            ? { assessment_id: j.pitch.assessmentId, status: "complete", captured_at: daysAgoIso(j.pitch.daysAgo), aeo_score: j.pitch.aeoScore, seo_score: j.pitch.seoScore, gated: false }
            : null,
        engagement_status: stage === "prospect" ? null : "signed",
        timeline_published_version: null,
    };
}

/**
 * Puts one journey sample at a stage: its business row, and (once signed) its
 * onboarding record. A prospect has no record. Saved by the caller.
 */
function seedJourneySample(j: JourneySample, stage: JourneyStage = j.stage) {
    const row = journeyRow(j, stage);
    const at = store.businesses.findIndex((b) => b.id === j.id);
    if (at >= 0) store.businesses[at] = row;
    else store.businesses.push(row);
    registerMockBusiness(row);
    delete store.records[j.id];
    if (stage === "prospect") return;

    const signedAt = daysAgoIso(j.signedDaysAgo);
    const paid = stage !== "not_paid";
    const o = freshRecord({ id: j.id, name: j.name, domain: j.domain }, { ...j.contact }, {
        ref: j.quote.ref, planName: j.quote.planName, termMonths: j.quote.termMonths, outlets: j.quote.outlets,
        startedAt: paid ? signedAt.slice(0, 10) : null,
        priorityKeywords: [...j.planKeywords], outletNames: [...j.planOutletNames],
    }, signedAt);
    o.buyMode = j.buyMode;
    o.outletAreas = [...j.outletAreas];
    o.form.link = formLinkFor(j.domain, o.outletAreas);
    // Not paid: no plan start, and the kick-off is held until the payment is recorded (call 18).
    if (!paid) o.paidAt = null;
    else o.sent.push(sentMessage("kickoff", { to: [o.contact], cc: [] }, signedAt));
    if (stage === "form_answered" || stage === "online_released") {
        const answeredAt = daysAgoIso(j.form.answeredDaysAgo);
        o.form.answeredAt = answeredAt;
        o.form.answers = journeyAnswers(j);
        o.form.knowledgeItems = 4;
        o.accessContact = { ...j.accessContact };
        o.siteBuilder = j.siteBuilder;
        if (j.form.areas.length) o.outletAreas = [...j.form.areas];
    }
    if (stage === "online_released") {
        // O9: an Online client gets the login and the access request without a baseline.
        const sentAt = daysAgoIso(Math.max(j.form.answeredDaysAgo - 1, 0));
        const to = { ...j.contact };
        const subdomain = j.name.replace(/[^a-z0-9]+/gi, "-").replace(/^-|-$/g, "").toLowerCase();
        o.login = { status: "sent", contact: to, sentAt, linkExpiresAt: new Date(new Date(sentAt).getTime() + 7 * DAY).toISOString(), signedInAt: null, workspaceUrl: `https://${subdomain}.staging.ourteam.ai` };
        o.sent.unshift(
            // An Online client's login goes before any check: its own words, never a check that has not run.
            sentMessage("login_invite", { to: [to], cc: [], subject: "Your Oura login is ready", body: LOGIN_INVITE_BODY(greetName(to.name), true) }, sentAt),
            sentMessage("access_request", {
                to: [{ ...j.accessContact }], cc: [to], subject: "2 invites we need, about 10 minutes",
                body: `Hi ${j.accessContact.name.split(" ")[0]}, ${j.contact.name.split(" ")[0]} named you for access. We never need a password.\n\n1. Google Search Console. Settings, Users and permissions, Add user. Enter oura@ourteam.ai as Full.\n\n2. Framer. Site settings, Collaborators, Invite. Enter oura@ourteam.ai as Editor.\n\nAngel`,
            }, sentAt),
        );
    }
    store.records[j.id] = o;
}

/** Seeds every journey sample not seeded before, at its own stage. Also fills a store saved before they existed. */
function seedJourney() {
    const seeded = new Set(store.journeySeeded ?? []);
    let changed = false;
    JOURNEY_SAMPLES.forEach((j) => {
        if (seeded.has(j.id)) return;
        seedJourneySample(j);
        seeded.add(j.id);
        changed = true;
    });
    store.journeySeeded = Array.from(seeded);
    if (changed) save();
}

/** The scan draft fixtures run here too (local dev): the first check's draft is on the record. */
const draftFixturesOn = () => scanDraftMode() === "fixtures";

/** A running sample scan, brought up to date: finished after MOCK_SCAN_MS. */
function settleScan(o: Onboarding): Onboarding {
    if (o.scan.status === "running" && o.scan.startedAt) {
        const elapsed = Date.now() - new Date(o.scan.startedAt).getTime();
        if (elapsed >= MOCK_SCAN_MS) {
            o.scan = { ...o.scan, status: "complete", percent: 100, finishedAt: new Date(new Date(o.scan.startedAt).getTime() + MOCK_SCAN_MS).toISOString(), assessmentId: o.scan.assessmentId ?? `${o.businessId}-first` };
        } else {
            o.scan.percent = Math.min(99, Math.round((elapsed / MOCK_SCAN_MS) * 100));
        }
    }
    return o;
}

/** How long the sample takes to make the Baseline Snapshot after the results are approved. */
const SNAPSHOT_MS = 12_000;

/** Brings a running sample scan up to date, and finishes onboarding when all access is held. */
/** How long the sample's login email stays `sending` (ourteam-backend#5523: a few seconds). */
const LOGIN_SENDING_MS = 4_000;

function settle(o: Onboarding): Onboarding {
    // A login email in flight reads `sent` once it has had its few seconds. Read
    // off the clock, not a timer, so a reload mid-send cannot leave it `sending`.
    if (o.login.status === "sending" && o.login.sentAt && Date.now() - new Date(o.login.sentAt).getTime() >= LOGIN_SENDING_MS) {
        o.login = { ...o.login, status: "sent" };
    }
    // The sample's Baseline Snapshot: ready SNAPSHOT_MS after the results were approved.
    if (o.resultsApprovedAt && !o.snapshotReadyAt && Date.now() - new Date(o.resultsApprovedAt).getTime() >= SNAPSHOT_MS) {
        o.snapshotReadyAt = new Date(new Date(o.resultsApprovedAt).getTime() + SNAPSHOT_MS).toISOString();
    }
    // A record stored before outlet areas were kept has none yet.
    o.outletAreas ??= o.form.answers?.outletAreas ?? [];
    settleScan(o);
    // O9: an Online client's first check waits for brand checks.
    o.scan.waitingFor = o.buyMode === "online" && o.scan.status === "not_started" ? "brand" : null;
    // O6: the First AI check's draft, drafted here once the form is in (O1 on the live backend).
    o.scan.draft = draftFixturesOn() ? firstCheckDraftFor(o.businessId) : o.scan.draft ?? null;
    o.access = o.access.map((a) => (a.key === "website" ? { ...a, role: SITE_ROLE[o.siteBuilder ?? "other"] } : a));
    if (o.scan.status === "complete") {
        showScanOnList(o);
        void pinBaseline(o);
    }
    // An Online client is never asked for a Google Business Profile: set-up finishes without it.
    const held = o.access.filter((a) => !(o.buyMode === "online" && a.key === "gbp")).every((a) => a.status === "connected" || a.status === "confirmed");
    // A local client reaches access only after its scan; an Online client gets access
    // BEFORE its first check (it waits for brand checks), so it is not complete until
    // that check has run (Jack, #4285).
    const firstCheckDone = o.scan.status === "complete";
    if (held && firstCheckDone && o.login.status !== "not_sent" && o.login.status !== "sending" && !o.completedAt) o.completedAt = nowIso();
    if (!held || !firstCheckDone) o.completedAt = null;
    return o;
}

/** The name a business already has in Oura, for the ones this store did not create. */
const FIXTURE_NAMES: Record<string, string> = {
    "pilates-collective": "The Pilates Collective",
    "nyonya-table": "Nyonya Table",
    "new-clinic": "Riverside Dental",
};
function knownName(businessId: string): string | null {
    return FIXTURE_NAMES[businessId] ?? store.businesses.find((b) => b.id === businessId)?.business_name ?? null;
}

/**
 * A client this store created has no scan of its own in the sample list. Once
 * its first AI check finishes, the row says so, with sample scores.
 */
/** A sample scan's scores, the same for a site every time it is scanned here (not a real formula). */
function sampleScores(domain: string): { aeo_score: number; seo_score: number } {
    const h = domain.split("").reduce((n, c) => n + c.charCodeAt(0), 0);
    return { aeo_score: 22 + (h % 20), seo_score: 41 + (h % 23) };
}

function showScanOnList(o: Onboarding) {
    const row = store.businesses.find((b) => b.id === o.businessId);
    const first = o.scan.assessmentId ?? `${o.businessId}-first`;
    // A pitched sample's row shows its pitch until the first check replaces it.
    if (!row || row.latest_assessment?.assessment_id === first) return;
    row.latest_assessment = {
        assessment_id: first,
        status: "complete",
        captured_at: o.scan.finishedAt ?? nowIso(),
        aeo_score: 34,
        seo_score: 51,
        gated: false,
    };
    registerMockBusiness(row);
}

function asMerchant(o: Onboarding): MerchantOnboarding {
    return {
        businessId: o.businessId, businessName: o.businessName, contactName: o.login.contact?.name ?? o.contact.name,
        outlets: o.plan?.outlets ?? 1, siteBuilder: o.siteBuilder, accountManager: o.accountManager,
        accessContact: o.accessContact, access: o.access, sharedWith: store.shared[o.businessId] ?? [], completedAt: o.completedAt,
        claimableAccess: ["gbp", "website", "gsc", "ga4"],
        waitingFor: o.scan.waitingFor === "brand" ? "brand" : null,
        firstCheck: {
            status:
                o.scan.status === "complete" ? (o.resultsSentAt === null ? "reviewing" : "ready")
                    : o.scan.status === "running" ? "running" : o.form.answeredAt ? "preparing" : "after_answers",
            finishedAt: o.scan.finishedAt ?? null,
        },
        // The sample store answers in-platform: it is what the one setup is built against.
        form: { link: o.form.link, answeredAt: o.form.answeredAt, inPlatform: true, ...setupDraftOf(o), answers: o.form.answers ?? null },
    };
}

/**
 * What the Setup page's questions open with: what they saved, else what Oura
 * already knows, from the pre-sale areas, the business's own services and the
 * person who signed. The backend fills these from the site and the Google
 * listing; the store guesses from the vertical.
 */
function setupDraftOf(o: Onboarding): Pick<MerchantForm, "draft" | "prefilled" | "savedAt"> {
    if (o.form.answers) {
        const a = o.form.answers;
        const answers: SetupAnswers = { outletAreas: a.outletAreas, services: a.services, customerQuestions: a.customerQuestions, goals: a.goals, plans: a.plans, approver: a.approver, approverEmail: a.approverEmail ?? "", other: a.other };
        return { draft: answers, prefilled: [], savedAt: o.form.answeredAt };
    }
    const guess = setupPrefill(o);
    if (!o.form.draft) return { ...guess, savedAt: null };
    // A filled-in answer the merchant has since changed is theirs.
    const draft = o.form.draft;
    const prefilled = guess.prefilled.filter((k) => JSON.stringify(draft[k]) === JSON.stringify(guess.draft[k]));
    return { draft, prefilled, savedAt: setupSaved[o.businessId] ?? null };
}
const setupSaved: Record<string, string> = {};

function setupPrefill(o: Onboarding): { draft: SetupAnswers; prefilled: SetupAnswerKey[] } {
    const row = store.businesses.find((r) => r.id === o.businessId || r.domain === o.domain);
    const pitched = startedPitchPrefill(o.domain)?.services ?? [];
    const services = pitched.length ? pitched : [...VERTICALS[verticalOf(row?.category, o.businessName, o.domain)].services];
    const draft: SetupAnswers = { outletAreas: [...o.outletAreas], services, customerQuestions: [], goals: [], plans: "", approver: o.contact.name, approverEmail: o.contact.email, other: "" };
    const prefilled = (["outletAreas", "services", "approver"] as const).filter((k) => draft[k].length > 0);
    return { draft, prefilled: [...prefilled] };
}

const notFound = { ok: false as const, code: "not_found", message: "This business has no onboarding record." };

/**
 * fixture-only: the scans a sample client has for "Use another scan" (#4773): its current first check,
 * an earlier complete one, one under another base_path (not offered), and a failed run (not offered).
 */
function sampleFirstCheckScans(o: Onboarding): AeoAssessment[] {
    const rows: AeoAssessment[] = [
        { assessment_id: `${o.businessId}-scan-earlier`, domain: o.domain, base_path: "", status: "complete", created_at: ago(40), completed_at: ago(39) },
        { assessment_id: `${o.businessId}-scan-blog`, domain: o.domain, base_path: "/blog", status: "complete", created_at: ago(20), completed_at: ago(19) },
        { assessment_id: `${o.businessId}-scan-failed`, domain: o.domain, base_path: "", status: "failed", created_at: ago(12), completed_at: ago(12) },
    ];
    if (o.scan.assessmentId) {
        rows.push({ assessment_id: o.scan.assessmentId, domain: o.domain, base_path: "", status: "complete",
            created_at: o.scan.startedAt ?? ago(10), completed_at: o.scan.finishedAt ?? ago(10) });
    }
    return rows.sort((a, b) => b.created_at.localeCompare(a.created_at));
}

/**
 * The sample question Oura picks for one signature part: one of the business's
 * own 50 when this session ran its first check, else a plain one for its area.
 */
function pickedSignature(o: Onboarding, part: "visibility" | "coverage", n?: number): string {
    const q = firstCheckQuestion(o.businessId, n ?? (part === "visibility" ? 1 : 2));
    if (q) return q;
    const area = o.outletAreas[0] ?? "town";
    return part === "visibility" ? `Is ${o.businessName} in ${area} any good?` : `Where can I book a same-day appointment in ${area}?`;
}

/** The plan holds 5 or more priority keywords. A sample whose plan never said holds them (as before). */
const keywordsHeld = (o: Onboarding) => (o.plan?.priorityKeywords ? o.plan.priorityKeywords.length >= 5 : true);

/**
 * The sample plan's signature and schedule. Picked by Oura when the first
 * check finishes (O4); samplespine.my is the one no AI answer names, so
 * staff choose its question.
 */
function planFactsOf(o: Onboarding): OnboardingPlanFacts {
    const held = store.planFacts?.[o.businessId];
    if (held) return { ...held, canPublish: held.signature === "held" && keywordsHeld(o) };
    if (o.scan.status !== "complete") return { signature: "pending", closest: null, published: false, canPublish: false };
    const nobody = o.domain === "samplespine.my";
    const facts: OnboardingPlanFacts = nobody
        ? { signature: "needs_person", closest: { n: 14, reason: "Gemini named it once." }, published: false, canPublish: false }
        : { signature: "held", closest: null, published: false, canPublish: keywordsHeld(o) };
    store.planFacts = { ...(store.planFacts ?? {}), [o.businessId]: facts };
    return facts;
}

/**
 * Fixtures only: the plan a signed client's record carries, also as the contract
 * Delivery and the timeline read (the server keeps one engagement for both). Without
 * it the Next card's "Open the timeline" landed on "No plan yet" (Angel, 15 Oct).
 */
const seededPlans = new Set<string>();
// A seed already on its way: a concurrent caller waits for it instead of reading no plan.
// The business is marked seeded only once the read or the seed has finished (Jack, #4291).
const planSeedInFlight = new Map<string, Promise<void>>();
async function ensurePlanSeeded(o: Onboarding): Promise<void> {
    const plan = o.plan;
    // Never against a live contract API: only the two sample stores are joined here.
    if (isClientReviewLive || !plan || seededPlans.has(o.businessId)) return;
    const active = planSeedInFlight.get(o.businessId);
    if (active) return active;
    const task = (async () => {
        try {
            if (!(await getEngagementIfExists(o.businessId))) {
                await seedEngagement(o.businessId, {
                    ref: plan.ref, client: o.businessName, planName: plan.planName, termMonths: plan.termMonths, pageUnits: 0,
                    outlets: plan.outlets, startedAt: plan.startedAt ?? o.paidAt, deliverables: [], deviations: [], documents: [],
                    ...(plan.priorityKeywords?.length ? { tracking: { priority_keywords: [...plan.priorityKeywords] } } : {}),
                });
            }
            seededPlans.add(o.businessId);
        } catch {
            // Left unmarked, so the next read tries again.
        } finally {
            planSeedInFlight.delete(o.businessId);
        }
    })();
    planSeedInFlight.set(o.businessId, task);
    return task;
}

/**
 * Fixtures only: the server pins the baseline once, when the first AI check
 * finishes (JOURNEY-SPEC §1 stage 6): `tracking.baseline_assessment_id` on the
 * plan, beside the keywords. Never moved here afterwards.
 */
const pinnedBaselines = new Set<string>();
const pinning = new Set<string>();
async function pinBaseline(o: Onboarding): Promise<void> {
    const id = o.scan.assessmentId;
    if (isClientReviewLive || !id || o.scan.status !== "complete" || pinnedBaselines.has(o.businessId) || pinning.has(o.businessId)) return;
    pinning.add(o.businessId);
    try {
        await ensurePlanSeeded(o);
        const e = await getEngagementIfExists(o.businessId);
        // No plan yet: leave it retryable rather than marking it pinned (Jack, #4291).
        if (!e) return;
        if (!e.tracking?.baseline_assessment_id) {
            await updateEngagement(
                o.businessId,
                { tracking: { ...(e.tracking ?? {}), baseline_assessment_id: id, baseline_at: o.scan.finishedAt } },
                "Oura",
                "Baseline pinned from the first AI check",
            );
        }
        pinnedBaselines.add(o.businessId);
    } catch {
        // Not pinned: the next read tries again.
    } finally {
        pinning.delete(o.businessId);
    }
}

const mock = {
    async list(): Promise<Onboarding[]> {
        // As the backend list does (#6086): each completed client's plan facts ride on its record.
        // A failed read leaves the key absent, the same as a failed batch on the server.
        const rows = await Promise.all(
            Object.values(store.records).map(settle).map(async (o): Promise<Onboarding> => {
                if (o.scan.status !== "complete") return { ...o };
                const planFacts = await mock.planFacts(o.businessId).catch(() => undefined);
                return planFacts === undefined ? { ...o } : { ...o, planFacts };
            }),
        );
        return delay(rows);
    },
    updateContact(b: string, patch: ContactPatch): Promise<ContactResult> {
        const o = store.records[b];
        if (!o) return delay({ ok: false as const, code: "not_found", message: "That client has no onboarding record.", field: null });
        // The server's own checks, in its own words, so the field errors are
        // reachable on fixtures too.
        if (patch.name === undefined && patch.email === undefined) {
            return delay({ ok: false as const, code: "invalid_request", message: "Give the contact's name, email, or both.", field: null });
        }
        const name = patch.name?.trim();
        const email = patch.email?.trim();
        if (patch.name !== undefined && !name) {
            return delay({ ok: false as const, code: "invalid_request", message: "The main contact needs a name.", field: "name" as const });
        }
        if (patch.email !== undefined && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email ?? "")) {
            return delay({ ok: false as const, code: "invalid_request", message: "The main contact needs an email address.", field: "email" as const });
        }
        o.contact = { name: name ?? o.contact.name, email: email ?? o.contact.email };
        store.records[b] = o;
        save();
        return delay({ ok: true as const, onboarding: settle(o) });
    },
    unmatched(): Promise<UnmatchedAnswer[]> {
        // Newest first, as the server returns them.
        return delay([...(store.unmatched ?? [])].sort((a, b) => (a.submittedAt < b.submittedAt ? 1 : -1)));
    },
    dismissUnmatched(rowId: string, reason: string): Promise<DismissAnswerResult> {
        const row = (store.unmatched ?? []).find((u) => u.id === rowId);
        if (!row) return delay({ ok: false as const, code: "already_dismissed", message: "That answer is no longer waiting. Reload the list." });
        if (!reason.trim()) return delay({ ok: false as const, code: "reason_required", message: "Say why this answer is dismissed." });
        store.unmatched = (store.unmatched ?? []).filter((u) => u.id !== rowId);
        save();
        return delay({ ok: true as const, answer: { ...row, dismissed: { at: new Date().toISOString(), by: "You", reason: reason.trim() } } });
    },
    assignUnmatched(rowId: string, businessId: string): Promise<AssignAnswerResult> {
        const row = (store.unmatched ?? []).find((u) => u.id === rowId);
        // Gone from the list means someone filed it already, which is the 409
        // the server answers — the sample store exercises the same path.
        if (!row) return delay({ ok: false as const, code: "already_filed", message: "That answer is already filed on a client. Reload the list." });
        const o = store.records[businessId];
        if (!o) return delay({ ok: false as const, code: "not_found", message: "That client has no onboarding record." });
        // A screen left open still offers a client who has answered since. The
        // UI no longer lists one, and nor does this: filing over an existing
        // answer would destroy it (Jack, #3991). The real route does not refuse
        // this yet — ourteam-backend#5329.
        if (o.form.answeredAt !== null) {
            return delay({ ok: false as const, code: "target_already_answered", message: "That client already has form answers. Pick a client still waiting for the form." });
        }
        // Filing it IS the answer arriving: the form reads answered from
        // `answeredAt`, so that and the answers themselves are what move.
        const answers = row.answers ?? o.form.answers;
        o.form = { ...o.form, answeredAt: row.submittedAt, answers };
        o.outletAreas = answers?.outletAreas?.length ? answers.outletAreas : o.outletAreas;
        store.unmatched = (store.unmatched ?? []).filter((u) => u.id !== rowId);
        store.records[businessId] = o;
        save();
        return delay({ ok: true as const, onboarding: settle(o) });
    },
    get(b: string): Promise<Onboarding | null> {
        const o = store.records[b];
        if (o) void ensurePlanSeeded(o);
        return delay(o ? settle(o) : null);
    },
    formLink(domain: string, areas: string[]): Promise<{ link: string }> {
        return delay({ link: formLinkFor(domain, areas) }, 80);
    },
    prefill(domain: string): Promise<OnboardingPrefill> {
        // A journey sample pitched on the platform: its pitched outlets, buy mode and date.
        const j = journeySample(domain);
        if (j?.pitch) return delay({ outletAreas: [...j.outletAreas], buyMode: j.buyMode, pitchedAt: daysAgoIso(j.pitch.daysAgo) }, 120);
        // A prospect scanned through New scan in this session: what its draft asked about.
        const started = startedPitchPrefill(domain);
        if (started) return delay(started, 120);
        return delay({ outletAreas: PRESALE_AREAS[domain.trim().toLowerCase()] ?? [] }, 120);
    },
    create(input: CreateClientInput): Promise<CreateClientResult> {
        const domain = input.domain.trim().toLowerCase();
        // O2: a prospect on this website is adopted, not refused.
        const adopted = !input.businessId
            ? store.businesses.find((b) => b.domain === domain && b.state === "prospect" && !store.records[b.id])
            : undefined;
        const businessId = input.businessId ?? adopted?.id;
        const id = businessId ?? domain;
        if (store.records[id]) {
            return delay({ ok: false as const, code: "already_onboarding", message: "This business is already being set up. Open it from the list." });
        }
        if (!businessId && (store.businesses.some((b) => b.domain === domain) || Object.values(store.records).some((r) => r.domain === domain))) {
            return delay({ ok: false as const, code: "domain_taken", message: "Another business already uses this website." });
        }
        const sameQuote = Object.values(store.records).find((r) => r.plan?.ref === input.engagement.ref);
        if (sameQuote) {
            return delay({ ok: false as const, code: "quote_taken", message: `Quote ${input.engagement.ref} is already on ${sameQuote.businessName}.` });
        }
        // A business already in Oura keeps its own name. A new one takes the quote's.
        // A prospect whose scan is still running is a business live (created at the start): it keeps its name too.
        const known = businessId ? knownName(businessId) : startedPitchName(domain);
        const name = known || input.engagement.client || domain;
        const at = nowIso();
        const o = freshRecord({ id, name, domain }, input.contact, {
            ref: input.engagement.ref, planName: input.engagement.planName, termMonths: input.engagement.termMonths,
            outlets: input.engagement.outlets, startedAt: input.engagement.startedAt,
        }, at);
        // Not paid: no paid date and no plan start; the kick-off is held until the payment (call 18).
        o.paidAt = input.paidOn ? new Date(`${input.paidOn}T00:00:00`).toISOString() : null;
        if (!input.paidOn && o.plan) o.plan = { ...o.plan, startedAt: null };
        o.outletAreas = input.outletAreas;
        o.buyMode = input.buyMode ?? journeySample(domain)?.buyMode ?? null;
        o.form.link = formLinkFor(domain, input.outletAreas);
        if (input.kickoff && input.paidOn) o.sent.unshift(sentMessage("kickoff", input.kickoff, at));
        store.records[id] = o;
        // A prospect this store holds (a journey sample) becomes a signed client on the list.
        const prospect = businessId ? store.businesses.find((b) => b.id === businessId) : undefined;
        if (prospect) {
            prospect.state = "client";
            prospect.engagement_status = "signed";
            registerMockBusiness(prospect);
        }
        if (!businessId) {
            // The category typed for it at the pitch stays (Angel, 15 Oct): a pitch still running here holds it.
            const pitched = startedPitchPrefill(domain);
            const row: AeoBusiness = {
                id, domain, business_name: name, category: pitched?.category ?? journeySample(domain)?.category ?? "", city: input.outletAreas[0] ?? "", state: "client", plan: null,
                created_at: at, latest_assessment: null, engagement_status: "signed", timeline_published_version: null,
            };
            store.businesses.push(row);
            registerMockBusiness(row);
        }
        // The plan exists the moment the client is signed, as the server writes it: Delivery reads it at once.
        return ensurePlanSeeded(o).then(() => delay({ ok: true as const, businessId: id, onboarding: o }, 600));
    },
    send(b: string, kind: MessageKind, draft: MessageDraft | KickoffDraft): Promise<OnboardingResult> {
        const o = store.records[b];
        if (!o) return delay(notFound);
        if (draft.to.length === 0) return delay({ ok: false as const, code: "no_recipient", message: "Add who this goes to." });
        o.sent.unshift(sentMessage(kind, draft, nowIso()));
        return delay({ ok: true as const, onboarding: settle(o) }, 500);
    },
    release(b: string, input: ReleaseInput): Promise<OnboardingResult> {
        const o = store.records[b];
        if (!o) return delay(notFound);
        if (input.login) {
            const email = input.login.email.trim().toLowerCase();
            if (email.includes("taken")) {
                return delay({ ok: false as const, code: "email_in_another_workspace", message: "That email already belongs to another workspace. Nothing was sent." });
            }
            const at = nowIso();
            const subdomain = o.businessName.replace(/[^a-z0-9]+/gi, "-").replace(/^-|-$/g, "").toLowerCase();
            // Sent, the way the server says it: `sending` while the email is on its way, then `sent` (settle).
            o.login = { status: "sending", contact: { name: input.login.name.trim(), email }, sentAt: at, linkExpiresAt: new Date(Date.now() + 7 * DAY).toISOString(), signedInAt: null, workspaceUrl: `https://${subdomain}.staging.ourteam.ai` };
            o.sent.unshift(sentMessage("login_invite", { to: [o.login.contact!], cc: [], subject: "Your Oura login is ready", body: LOGIN_INVITE_BODY(greetName(o.login.contact!.name), o.buyMode === "online" && o.scan.status === "not_started") }, at));
        }
        if (input.accessRequest) o.sent.unshift(sentMessage("access_request", input.accessRequest, nowIso()));
        return delay({ ok: true as const, onboarding: settle(o) }, 700);
    },
    resendLogin(b: string): Promise<OnboardingResult> {
        const o = store.records[b];
        if (!o) return delay(notFound);
        // The server's own refusals, so both are reachable on fixtures.
        if (o.login.status === "not_sent" || !o.login.contact) {
            return delay({ ok: false as const, code: "no_login", message: "No login has been sent yet, so there is nothing to send again." });
        }
        if (o.login.status === "signed_in") {
            return delay({ ok: false as const, code: "already_signed_in", message: "This client has already signed in, so there is nothing left to send." });
        }
        const to = o.login.contact;
        const at = nowIso();
        // A resend replaces the link: the earlier one stops working.
        o.login = { ...o.login, status: "sent", failureDetail: null, sentAt: at, linkExpiresAt: new Date(Date.now() + 7 * DAY).toISOString() };
        o.sent.unshift(sentMessage("login_invite", { to: [to], cc: [], subject: "Your Oura login is ready", body: LOGIN_INVITE_BODY(greetName(to.name), o.buyMode === "online" && o.scan.status === "not_started") }, at));
        return delay({ ok: true as const, onboarding: settle(o) }, 700);
    },
    confirm(b: string, key: AccessKey, confirmed: boolean): Promise<OnboardingResult> {
        const o = store.records[b];
        if (!o) return delay(notFound);
        o.access = o.access.map((a) => {
            if (a.key !== key) return a;
            if (confirmed) return { ...a, status: "confirmed", since: nowIso(), detail: `Confirmed by ${ANGEL.name}` };
            return { ...a, status: a.claimedAt ? "claimed" : "not_started", since: null, detail: null };
        });
        return delay({ ok: true as const, onboarding: settle(o) });
    },
    startScan(b: string, inputs: ScanInputs): Promise<OnboardingResult> {
        const o = store.records[b];
        if (!o) return delay(notFound);
        if (inputs.services.length === 0 || inputs.outletAreas.length === 0) {
            return delay({ ok: false as const, code: "invalid_request", message: "Add at least 1 service and 1 outlet area." });
        }
        o.outletAreas = inputs.outletAreas;
        o.scan = { status: "running", assessmentId: null, startedAt: nowIso(), finishedAt: null, percent: 0, failure: null };
        return delay({ ok: true as const, onboarding: settle(o) });
    },
    recordPayment(b: string, paidOn: string): Promise<OnboardingResult> {
        const o = store.records[b];
        if (!o) return delay(notFound);
        if (!paidOn) return delay({ ok: false as const, code: "invalid_request", message: "Add the date the payment cleared." });
        o.paidAt = new Date(`${paidOn}T00:00:00`).toISOString();
        if (o.plan) o.plan = { ...o.plan, startedAt: paidOn };
        if (isClientReviewLive) return delay({ ok: true as const, onboarding: settle(o) });
        // The sample's ONE shared plan: the engagement was seeded at signing with no start
        // date and is never seeded again, so the start has to reach it here, or the Timeline
        // still sees an unstarted plan after payment was recorded (Jack, #4285).
        return ensurePlanSeeded(o).then(async () => {
            const engagement = await getEngagementIfExists(b);
            // The seed swallows its own failures: no engagement after it means the plan never
            // started, so the catch below refuses instead of reporting success (Jack, #4298).
            if (!engagement) throw new Error("The plan engagement was not created");
            if (engagement.startedAt !== paidOn) {
                await updateEngagement(b, { startedAt: paidOn }, "Oura", "Plan started when payment was recorded");
            }
            return { ok: true as const, onboarding: settle(o) };
        }).catch((err: unknown) =>
            // The plan did not start: say so rather than report a payment the Timeline contradicts.
            refusal(err, "The payment was recorded, but the plan did not start. Try again in a moment.", "recordPayment"),
        );
    },
    planFacts(b: string): Promise<OnboardingPlanFacts | null> {
        const o = store.records[b];
        if (!o) return delay(null, 120);
        const facts = planFactsOf(settle(o));
        if (isClientReviewLive) return delay(facts, 120);
        // One plan for both pages: the Next card reads the contract and the Timeline the way
        // the live read does, so it never says "held" while the Timeline says missing.
        return ensurePlanSeeded(o)
            .then(async () => {
                let e = await getEngagementIfExists(b).catch(() => null);
                const sig = e?.tracking?.signature;
                // Oura picked the pair from the first check (O4): the server writes it to the contract.
                if (e && facts.signature === "held" && !(sig?.visibility && sig?.coverage)) {
                    const picked = { visibility: sig?.visibility || pickedSignature(o, "visibility"), coverage: sig?.coverage || pickedSignature(o, "coverage") };
                    e = await updateEngagement(b, { tracking: { ...(e.tracking ?? {}), signature: picked } }, "Oura", "Signature questions picked from the first AI check").catch(() => e);
                }
                const t = await getTimeline(b).catch(() => null);
                const held = !!(e?.tracking?.signature?.visibility && e?.tracking?.signature?.coverage);
                return {
                    ...facts,
                    signature: held ? "held" : facts.signature === "needs_person" ? "needs_person" : "pending",
                    // A restarted demo merchant's plan is published only by this journey's own press, not the sample Timeline's history.
                    published: facts.published || (!!t?.published && !(o.resultsSentAt === null && o.businessId === DEMO_MERCHANT_BUSINESS)),
                    canPublish: o.scan.status === "complete" && !!t && timelinePublishable(t),
                    blockers: t ? publishBlockers(t) : [],
                } satisfies OnboardingPlanFacts;
            });
    },
    firstCheckScans(b: string, basePath: string): Promise<FirstCheckScansResult> {
        const o = store.records[b];
        if (!o) return delay({ ok: false as const, message: "This business has no onboarding record." });
        // As the server answers it: this site's complete scans only.
        const scans = sampleFirstCheckScans(o).filter((s) => s.status === "complete" && (s.base_path ?? "") === basePath);
        return delay({ ok: true as const, scans });
    },
    chooseFirstCheck(b: string, assessmentId: string): Promise<OnboardingResult> {
        const o = store.records[b];
        if (!o) return delay(notFound);
        // The server's order: the scan is this business's first, then complete, then the check's state.
        const scan = sampleFirstCheckScans(o).find((s) => s.assessment_id === assessmentId);
        if (!scan || (scan.base_path ?? "") !== "") {
            return delay({ ok: false as const, code: "not_this_business", message: "That scan is not a scan of this business." });
        }
        if (scan.status !== "complete") {
            return delay({ ok: false as const, code: "scan_not_complete", message: "Only a completed scan can be the First AI check." });
        }
        if (o.scan.assessmentId === assessmentId) return delay({ ok: true as const, onboarding: settle(o) });
        if (o.scan.status === "running") {
            return delay({ ok: false as const, code: "scan_running", message: "The first AI check is already running." });
        }
        if (o.resultsSentAt) {
            return delay({ ok: false as const, code: "results_already_sent", message: "The results were already sent to the client, so the First AI check is locked." });
        }
        // As an Edit and rescan does: the approval, the Snapshot and the plan start again from this scan.
        o.scan = { ...o.scan, assessmentId, status: "complete", startedAt: scan.created_at, finishedAt: scan.completed_at ?? null, percent: 100, failure: null };
        o.resultsApprovedAt = null;
        o.snapshotReadyAt = null;
        if (store.planFacts) delete store.planFacts[b];
        o.firstCheckSetBy = { name: o.accountManager?.name || "Oura staff", at: nowIso() };
        return delay({ ok: true as const, onboarding: settle(o) });
    },
    chooseSignature(b: string, question: number): Promise<OnboardingResult> {
        const o = store.records[b];
        if (!o) return delay(notFound);
        if (!Number.isInteger(question) || question < 1 || question > 50) {
            return delay({ ok: false as const, code: "not_in_set", message: "Pick one of the 50 questions." });
        }
        const facts = planFactsOf(o);
        store.planFacts = { ...(store.planFacts ?? {}), [b]: { ...facts, signature: "held", closest: null, canPublish: keywordsHeld(o) } };
        // The server stores the chosen question on the contract (O4): the plan and the Timeline read it there.
        if (!isClientReviewLive) {
            return ensurePlanSeeded(o)
                .then(() => getEngagementIfExists(b))
                .then((e) => {
                    if (!e) return undefined;
                    const signature = { visibility: pickedSignature(o, "visibility", question), coverage: e.tracking?.signature?.coverage || pickedSignature(o, "coverage") };
                    return updateEngagement(b, { tracking: { ...(e.tracking ?? {}), signature } }, "Oura", `Q${question} chosen as the signature question`);
                })
                .catch(() => undefined)
                .then(() => ({ ok: true as const, onboarding: settle(o) }));
        }
        return delay({ ok: true as const, onboarding: settle(o) });
    },
    mine(): Promise<MerchantOnboarding | null> {
        const o = store.records[DEMO_MERCHANT_BUSINESS];
        return delay(o ? asMerchant(settle(o)) : null);
    },
    claim(key: AccessKey, claimed: boolean, platform?: SiteBuilder): Promise<MerchantOnboarding> {
        const o = store.records[DEMO_MERCHANT_BUSINESS];
        if (key === "website" && claimed && platform) o.siteBuilder = platform;
        o.access = o.access.map((a) => {
            if (a.key !== key) return a;
            // A claim never undoes what staff confirmed or Oura connected.
            if (a.status === "confirmed" || a.status === "connected") return a;
            return claimed
                ? { ...a, status: "claimed", claimedAt: nowIso(), claimedBy: o.login.contact?.name ?? o.contact.name }
                : { ...a, status: "not_started", claimedAt: null, claimedBy: null };
        });
        return delay(asMerchant(settle(o)));
    },
    connect(key: AccessKey, payload: MerchantConnectPayload): Promise<MerchantConnectResult> {
        const o = store.records[DEMO_MERCHANT_BUSINESS];
        void payload;
        // Invites are claimed and confirmed. Only Google's own approval connects.
        if (key !== "gsc" && key !== "ga4") {
            return delay({ ok: false as const, message: "This one is given by invite." });
        }
        o.access = o.access.map((a) => (a.key === key ? { ...a, status: "connected", since: nowIso(), detail: `Reading ${o.domain}` } : a));
        return delay({ ok: true as const, kind: "connected" as const, onboarding: asMerchant(settle(o)) }, 900);
    },
    disconnect(key: AccessKey): Promise<MerchantOnboarding> {
        const o = store.records[DEMO_MERCHANT_BUSINESS];
        o.access = o.access.map((a) => (a.key === key ? { ...a, status: "not_started", since: null, claimedAt: null, claimedBy: null, detail: null } : a));
        return delay(asMerchant(settle(o)));
    },
    share(person: OnboardingPerson, steps?: SetupStepKey[]): Promise<MerchantOnboarding> {
        const o = store.records[DEMO_MERCHANT_BUSINESS];
        store.shared[o.businessId] = [{ ...person, at: nowIso(), ...(steps ? { steps } : {}) }, ...(store.shared[o.businessId] ?? [])];
        return delay(asMerchant(settle(o)), 500);
    },
    saveAnswers(answers: Partial<SetupAnswers>): Promise<MerchantOnboarding> {
        const o = store.records[DEMO_MERCHANT_BUSINESS];
        // Answered is answered: a late save from an open tab changes nothing.
        if (!o.form.answeredAt) {
            o.form.draft = { ...(setupDraftOf(o).draft ?? setupPrefill(o).draft), ...answers };
            setupSaved[o.businessId] = nowIso();
        }
        return delay(asMerchant(settle(o)), 250);
    },
    submitAnswers(answers: SetupAnswers): Promise<SubmitAnswersResult> {
        const o = store.records[DEMO_MERCHANT_BUSINESS];
        const fields: Partial<Record<SetupAnswerKey, string>> = {};
        // The page's own required set, so the sample refuses what the backend will.
        for (const q of missingAnswers(answers)) fields[q.key] = "This one is needed.";
        if (Object.keys(fields).length > 0) return delay({ ok: false as const, message: "A few answers are missing.", fields });
        if (!o.form.answeredAt) {
            const person = o.login.contact ?? o.contact;
            o.form.answeredAt = nowIso();
            o.form.answers = { ...answers, contactName: person.name, contactEmail: person.email, accessContact: o.accessContact, files: [] };
            o.form.draft = null;
            o.form.knowledgeItems = 4;
            o.outletAreas = [...answers.outletAreas];
        }
        return delay({ ok: true as const, onboarding: asMerchant(settle(o)) }, 600);
    },
};

/**
 * Sample data only: stand in for what the merchant and Oura do, so every
 * state of the staff screen can be reached without a backend.
 */
export const onboardingSim = {
    available: sampleStoreOn,
    /**
     * The demo merchant as a one-setup client on the day the Setup email went:
     * signed in, nothing answered, nothing given. What the merchant's Setup
     * page opens on for someone new.
     */
    restartMySetup() {
        const o = store.records[DEMO_MERCHANT_BUSINESS];
        if (!o) return;
        const sent = ago(0, 2);
        o.flow = "one_setup";
        o.form = { ...o.form, answeredAt: null, answers: null, knowledgeItems: null, draft: null };
        o.scan = { status: "not_started", assessmentId: null, startedAt: null, finishedAt: null, percent: null, failure: null };
        o.resultsSentAt = null;
        o.resultsPack = null;
        o.resultsApprovedAt = null;
        o.snapshotReadyAt = null;
        // The plan is drafted again from the new first check: nothing published yet.
        if (store.planFacts) delete store.planFacts[o.businessId];
        o.login = { ...o.login, status: "signed_in", sentAt: sent, signedInAt: nowIso() };
        o.access = freshAccess();
        o.completedAt = null;
        o.sent = [sentMessage("kickoff", { to: [o.contact], cc: [] }, sent)];
        store.shared[o.businessId] = [];
        delete setupSaved[o.businessId];
        save();
    },
    formAnswered(b: string) {
        const o = store.records[b];
        if (!o || o.form.answeredAt) return;
        const j = journeySample(o.domain);
        if (j) {
            // A journey sample answers as its catalogue says: the first read then drafts its First AI check.
            o.form.answeredAt = nowIso();
            o.form.answers = journeyAnswers(j);
            o.form.knowledgeItems = 4;
            o.accessContact = { ...j.accessContact };
            o.siteBuilder = j.siteBuilder;
            if (j.form.areas.length) o.outletAreas = [...j.form.areas];
            save();
            return;
        }
        const accessContact: AccessContact = { name: "Daniel Tan", email: "daniel@webstudio.example" };
        o.form.answeredAt = nowIso();
        const areas = o.outletAreas.length > 0 ? o.outletAreas : ["Bangsar", "Mont Kiara"];
        // The business's own vertical: its pitch's services when one ran here, else its category, name and website.
        const row = store.businesses.find((r) => r.id === b || r.domain === o.domain);
        const pitched = startedPitchPrefill(o.domain)?.services ?? [];
        const services = pitched.length ? pitched : [...VERTICALS[verticalOf(row?.category, o.businessName, o.domain, o.buyMode === "online" ? "online" : "")].services];
        o.form.answers = sampleAnswers(o.contact, areas, services, accessContact);
        o.form.knowledgeItems = 4;
        o.accessContact = accessContact;
        o.siteBuilder = "wix";
        save();
    },
    /** Staff approved the check's results: in the sample, Oura makes the Baseline Snapshot SNAPSHOT_MS later. */
    resultsApproved(b: string) {
        const o = store.records[b];
        if (!o || o.scan.status !== "complete" || o.resultsApprovedAt) return;
        o.resultsApprovedAt = nowIso();
        // Oura takes a while to make it: the sample finishes it SNAPSHOT_MS later, on a read.
        o.snapshotReadyAt = null;
        save();
    },
    /** Staff uploaded the execution timeline: Oura's schedule is published. With a version, Oura read the file. */
    timelineUploaded(b: string, version?: string) {
        const o = store.records[b];
        if (!o) return;
        o.resultsPack = version ? { timelineAt: nowIso(), timelineVersion: version } : { timelineAt: nowIso() };
        store.planFacts = { ...(store.planFacts ?? {}), [b]: { ...planFactsOf(o), published: true } };
        save();
    },
    /** Staff sent the results and the plan: the merchant's results are theirs to see. */
    resultsSent(b: string) {
        const o = store.records[b];
        if (!o || o.scan.status !== "complete" || o.resultsSentAt) return;
        o.resultsSentAt = nowIso();
        settle(o);
        save();
    },
    scanFinished(b: string) {
        const o = store.records[b];
        if (!o || o.scan.status !== "running") return;
        // The run the record holds (the draft's start, or the pitch it took over) is the one that finishes.
        o.scan = { ...o.scan, status: "complete", percent: 100, finishedAt: nowIso(), assessmentId: o.scan.assessmentId ?? `${b}-first` };
        save();
        // One clock: every scan on this site that started earlier (the pitch) has finished by now too.
        if (draftFixturesOn()) finishScansFor(o.domain, Date.now());
        settle(o);
        save();
    },
    signedIn(b: string) {
        const o = store.records[b];
        if (!o || o.login.status !== "sent") return;
        o.login = { ...o.login, status: "signed_in", signedInAt: nowIso() };
        save();
    },
    /** The merchant connects what Oura can test, and says the Google profile invite is sent. */
    accessGiven(b: string) {
        const o = store.records[b];
        if (!o) return;
        o.access = o.access.map((a) => {
            if (a.status === "connected" || a.status === "confirmed") return a;
            // The invites are asked of whoever looks after access (Daniel Tan at Riverside): that person says done.
            if (a.key === "gbp" || a.key === "website") return { ...a, status: "claimed", claimedAt: nowIso(), claimedBy: o.accessContact?.name || o.login.contact?.name || o.contact.name };
            return { ...a, status: "connected", since: nowIso(), detail: `Reading ${o.domain}` };
        });
        // Connecting happens on the client's own page, so they have signed in by then.
        if (o.login.status === "sent") o.login = { ...o.login, status: "signed_in", signedInAt: nowIso() };
        save();
    },
    /** Staff publish the schedule on the Timeline (which runs on its own sample data). */
    published(b: string) {
        const o = store.records[b];
        if (!o) return;
        store.planFacts = { ...(store.planFacts ?? {}), [b]: { ...planFactsOf(o), published: true } };
        // The business row says it too, so the list's row action stops offering Publish.
        store.businesses = store.businesses.map((r) => (r.id === b ? { ...r, timeline_published_version: (r.timeline_published_version ?? 0) + 1 } : r));
        save();
    },
    /** The payment cleared: the plan starts today and the held kick-off can go (Record the payment). */
    paid(b: string) {
        const o = store.records[b];
        if (!o || o.paidAt) return;
        o.paidAt = nowIso();
        if (o.plan) o.plan = { ...o.plan, startedAt: nowIso().slice(0, 10) };
        save();
    },
    /**
     * Puts a journey sample (samplesmile.my, samplespine.my, samplesight.my,
     * sampletea.my, samplekids.my) at a stage, so its walk can start again:
     * `prospect` (scanned, not signed), `not_paid`, `kickoff_sent`,
     * `form_answered` or `online_released`. Its First AI check drafts go too.
     */
    journey(b: string, stage: JourneyStage) {
        const j = journeySample(b);
        if (!j) return;
        if (draftFixturesOn()) forgetFirstCheckDrafts(j.id);
        if (store.planFacts) delete store.planFacts[j.id];
        seedJourneySample(j, stage);
        save();
    },
    reset() {
        // The plans of every client this store signed go too: signed again, each reads its own quote.
        if (!isClientReviewLive) forgetSampleEngagements([...Object.keys(store.records), ...JOURNEY_SAMPLES.map((j) => j.id)].filter((id) => !FIXTURE_NAMES[id]));
        seededPlans.clear();
        planSeedInFlight.clear();
        pinnedBaselines.clear();
        pinning.clear();
        store = emptyStore();
        seedDemo();
        seedJourney();
        store.businesses.forEach(registerMockBusiness);
    },
};

/*
 * The scan draft fixtures read a signed client's facts here, and tell this
 * store when its First AI check starts (fixtures/journey.ts).
 */
const onboardingSide = {
    facts(b: string): SignedFacts | null {
        const o = store.records[b];
        if (!o) return null;
        settleScan(o);
        const a = o.form.answers;
        return {
            businessId: o.businessId,
            domain: o.domain,
            businessName: o.businessName,
            contactName: a?.contactName || o.contact.name,
            answeredAt: o.form.answeredAt,
            form: a ? { services: [...a.services], areas: [...a.outletAreas], customerQuestions: [...a.customerQuestions], goals: [...(a.goals ?? [])], plans: a.plans ?? "", other: a.other ?? "" } : null,
            quote: o.plan ? { ref: o.plan.ref, outlets: o.plan.outlets, areas: [...o.outletAreas] } : null,
            heldKeywords: [...(o.plan?.priorityKeywords ?? [])],
            heldOutletNames: [...(o.plan?.outletNames ?? [])],
            buyMode: o.buyMode ?? null,
            firstCheck: { status: o.scan.status, assessmentId: o.scan.assessmentId, finishedAt: o.scan.finishedAt },
        };
    },
    /**
     * A pitch finished on the sample clock. Its business row reads the scored
     * scan: a new prospect row when the list has none, else the row it already
     * has (a client signed while the pitch ran keeps a later scan when it has one).
     */
    pitchFinished(run: PitchRunDone) {
        const domain = run.domain.trim().toLowerCase();
        const scored: NonNullable<AeoBusiness["latest_assessment"]> = {
            assessment_id: run.assessmentId, status: "complete", captured_at: run.finishedAt, ...sampleScores(domain), gated: false,
        };
        let row = store.businesses.find((r) => r.domain === domain);
        if (!row) {
            row = {
                id: domain, domain, business_name: run.businessName, category: run.category, city: run.city, state: "prospect", plan: null,
                created_at: run.startedAt, latest_assessment: scored, engagement_status: null, timeline_published_version: null,
            };
            store.businesses.push(row);
        } else if (!row.latest_assessment || row.latest_assessment.captured_at < run.finishedAt) {
            row.latest_assessment = scored;
        }
        registerMockBusiness(row);
        save();
    },
    /** H4-a: the draft's start is the first AI check. It finishes MOCK_SCAN_MS later, and the baseline is held. */
    firstCheckStarted(b: string, run: FirstCheckRun) {
        const o = store.records[b];
        if (!o) return;
        // Taking over the pitch scan still running on the same 50: it finishes when the pitch does.
        o.scan = { ...o.scan, status: "running", assessmentId: run.assessmentId, startedAt: run.startedAt ?? nowIso(), finishedAt: null, percent: 0, failure: null };
        if (run.outletAreas.length) o.outletAreas = [...run.outletAreas];
        if (o.plan && run.priorityKeywords) o.plan = { ...o.plan, priorityKeywords: [...run.priorityKeywords] };
        if (o.plan && run.outletNames) o.plan = { ...o.plan, outletNames: [...run.outletNames] };
        save();
        // The plan exists from the signing on: the keywords go onto it at Start, as the server writes them.
        const keywords = run.priorityKeywords;
        if (keywords?.length && !isClientReviewLive) {
            void getEngagementIfExists(b)
                .then((e) => (e ? updateEngagement(b, { tracking: { ...(e.tracking ?? {}), priority_keywords: [...keywords] } }, "Oura", "Priority keywords from the first AI check") : undefined))
                .catch(() => undefined);
        }
    },
};

if (sampleStoreOn) registerOnboardingSide(onboardingSide);

// A schedule published on the Timeline (its own sample store): the business row says so, as the server's
// list does, so the row stops offering Publish and the "Schedule not published" count drops.
if (sampleStoreOn && !isClientReviewLive) {
    onSampleTimelinePublished((b, version) => {
        const row = store.businesses.find((r) => r.id === b);
        if (!row || (row.timeline_published_version ?? 0) >= version) return;
        row.timeline_published_version = version;
        registerMockBusiness(row);
        save();
    });
}

if (typeof window !== "undefined" && sampleStoreOn) {
    seedDemo();
    seedJourney();
    // Rows this store created have to be in the Businesses list after a reload too.
    store.businesses.forEach(registerMockBusiness);
    (window as unknown as { __ouraOnboardingSim?: typeof onboardingSim }).__ouraOnboardingSim = onboardingSim;
}
